// Monitor de Custos — Fornecedores (Cotacoes > Monitor de Custos).
// Familias <details> recolhiveis, coluna Fornecedor com selo, celula do codigo pintada pelo fornecedor, linha MELHOR amarela com moldura+selo
// (daltonismo-safe), Custo unit. / Sugestao de Venda, markup editavel por familia, cupom no topo,
// descricao do fornecedor em 2 linhas, e o rodape com a MEDIA + o botao que a aplica nos produtos
// (DA-642). ⚠️ As colunas "Nosso Pix" e "Variacao" do mockup original NAO existem desde a DA-304.
// Cores 100% via tokens do tema (sai certo no Cobre e no Azul Royal). Numeros em IBM Plex Mono.
// A conta de dinheiro vem PRONTA do backend.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { RefreshCw, Plus, Trash2, Pencil, X, Search, Check, GripVertical, ChevronUp, ChevronDown, ImagePlus, BookmarkPlus, BookmarkCheck, Link2, ArrowRightLeft } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { api } from '../../lib/api';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { UpperInput } from '../../components/inputs/UpperInput';
import { ComboBox } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { FotoProduto } from '../../components/FotoProduto';
import { contar, formatMoedaSemSimbolo, formatQtd, ALIQUOTAS_DIFAL, fatorDoRepresentante } from '@fiodecobre/shared';
// Vincular reaproveita o cadastro REAL do catalogo (revisar + Salvar), nao um seletor proprio.
import { ModalEditarCatalogoItem } from './CatalogoFornecedor';
// Componente UNICO de progresso (HUD "CORE SYNC") usado nos 3 gatilhos: Atualizar geral, Buscar e Atualizar por familia.
import { ProgressoCoreSync } from './ProgressoCoreSync';
// Foto colada por CODIGO do fornecedor (o nosso produto nao distingue rolo de bobina).
import { ModalFotoOferta } from './ModalFotoOferta';
// Regua de preco + aplicacao da media nos produtos do cadastro (DA-642).
import { ModalAtualizarPrecos } from './ModalAtualizarPrecos';

interface OfertaApi {
  oferta_id: number;
  fornecedor_id: number;
  fornecedor_nome: string;
  codigo_fornecedor: string;
  uf: string;
  qtd: number;
  marca: string | null;
  marca_exibida: string | null; // marca do cadastro OU, na falta dela, a lida na descricao do site
  // foto ja RESOLVIDA: colada nesta oferta > produto vinculado no catalogo do fornecedor.
  foto_url: string | null;
  foto_propria: boolean; // true = foto colada NESTE codigo (a unica que distingue rolo de bobina)
  foto_tem_original: boolean;         // o arquivo original foi guardado (0200) — so ele vira Master
  foto_promovida_url: string | null;  // ja virou foto do acervo de produtos (0201)
  // De-para DESTE codigo -> nosso produto (0203). Nao e o produto da familia (raiz, sem cor).
  produto_id: number | null;
  produto_codigo: string | null;
  produto_descricao: string | null;
  produto_estoque?: number | null; // estoque do produto vinculado a ESTA linha
  // O preco que a linha EXIBE: o da coleta corrente e, quando ela nao tem, o digitado (0289).
  preco_embalagem: number | null;
  // O digitado CRU (vem mesmo quando a coleta esta mandando) — e o que o formulario mostra, pelo
  // mesmo motivo que `descricao` fica separada de `descricao_fornecedor`.
  preco_manual: number | null;
  preco_manual_em: string | null;
  // true = a linha esta exibindo o digitado; a coluna COLETADO carimba "digitado DD/MM" em vez da
  // data do robo. Nunca e' so uma questao de cor: o texto e que separa as duas origens.
  usa_preco_manual: boolean;
  // Ultima leitura que o robo conseguiu fazer (codigo confirmado), de qualquer idade. So lastro pro
  // formulario — nao entra em conta nenhuma.
  ultimo_preco_coletado: number | null;
  ultimo_preco_coletado_em: string | null;
  // A DIGITADA no cadastro (0246) — crua, sem fallback. E o que o formulario de edicao mostra,
  // pelo mesmo motivo que `marca` fica separada de `marca_exibida`.
  descricao: string | null;
  // O que a COLUNA exibe: a digitada acima e, na falta dela, a ultima coleta com codigo confirmado.
  descricao_fornecedor: string | null;
  disponivel: boolean;
  motivo: string | null; // 'codigo_nao_encontrado' = site devolveu outro produto (codigo do cadastro mudou)
  // So com o codigo nao encontrado: a busca pela DESCRICAO achou o mesmo nome com OUTRO codigo
  // ([DA-797]). E' sugestao pra ele conferir — o cadastro nao muda sozinho.
  sugestao_codigo?: string | null;
  sugestao_descricao?: string | null;
  custo_unit: number | null;
  // Aliquota do DIFAL escolhida na caixa da linha (0290, [DA-727]). null = a conferir: o custo de MG
  // usa a estimativa de 4,49%.
  difal_pct: number | null;
  // [DA-804] Linha de robo de VAREJO (Tramontina): o preco e o a vista do site e o custo e ESTIMADO
  // por ele x fator. `fator_site_pct` = o proprio da linha (cru, o form edita; null = o do fornecedor);
  // `fator_aplicado_pct` = o que entrou na conta.
  preco_de_site?: boolean;
  fator_site_pct?: number | null;
  fator_aplicado_pct?: number | null;
  // A origem do fator da linha (0315): o preco do representante que ele digitou, o do site usado na
  // conta e quando. O servidor calcula o fator; a tela so mostra.
  preco_representante?: number | null;
  // true = o custo E o preco do representante (linha de varejo sem site de referencia).
  custo_do_representante?: boolean;
  // true = o preco do representante digitado nao fechou com o site lido (fator fora de 0-100%).
  representante_nao_fecha?: boolean;
  preco_site_referencia?: number | null;
  fator_calibrado_em?: string | null;
  fc_pix_unit: number | null; // Sugestao de Venda (custo x markup)
  coletado_em: string | null;
  coleta_solicitada_em: string | null; // pedido de coleta pendente DESTA oferta (botao "Atualizar" da linha)
  no_catalogo: boolean; // ja salva/vinculada no Catalogo do Fornecedor (DA-370)
  melhor: boolean;
}

// Produto do cadastro (busca do autocomplete de vinculo).
interface ProdutoBusca { id: number; codigo_interno: string; descricao: string; foto_url: string | null; status?: string | null }

// Cor da CELULA DO CODIGO (e das setas) pelo fornecedor (pedido dele, 07/10/2026) — par fundo+fonte fixo, nunca
// variavel de tema (a fonte tem que contrastar com ESTE fundo em qualquer tema). Azul #2563eb: o
// branco da 5,2:1 e a moldura preta da melhor oferta ainda aparece sobre ele. Outro fornecedor = sem cor.
function corDoFornecedor(nome: string | null | undefined): { bg: string; fg: string } | null {
  const n = (nome || '').toUpperCase();
  if (n.includes('MARTINS')) return { bg: '#2563eb', fg: '#fff' };
  // Turquesa (amostra dele, 07/10): o amarelo confundia com o selo/realce de menor preco.
  if (n.includes('TAMBASA')) return { bg: '#00ffff', fg: '#000' };
  // Cores da marca (pedido dele, 08/10/2026): fundo branco + o azul do logo da Tramontina, medido
  // no print do site (#0000B7) — 12,6:1.
  if (n.includes('TRAMONTINA')) return { bg: '#fff', fg: '#0000b7' };
  return null;
}

// Percentual digitado ([DA-804]): aceita "45,50%", "45,5", "45.5" e " 46,28 % ". Com virgula, o
// ponto e milhar; sem virgula, o ponto e a casa decimal (senao "45.5" virava 455). Vazio/lixo = NaN.
function lerPct(s: string): number {
  const t = s.replace(/%/g, '').replace(/\s+/g, '');
  if (!t) return NaN;
  return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
}

// Miniatura da lista de busca de produto (os dois vinculos: familia e oferta). Sem foto, uma
// moldura tracejada do mesmo tamanho — senao as descricoes ficam desalinhadas entre as linhas.
function MiniFotoBusca({ url }: { url: string | null }) {
  if (url) return <FotoProduto url={url} tam={36} style={{ flexShrink: 0, borderRadius: 4 }} />;
  return <span style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 4, border: '1px dashed var(--border)' }} />;
}

// Familia = NOME + markup + os codigos de fornecedor que a gente compara. O nome e a unica fonte do
// rotulo (migration 0130) e o monitor compara o CUSTO das origens entre si — o preco que cobramos
// nao entra na conta. O `produto` (migration 0199) e um apontador OPCIONAL pro cadastro: diz a que
// produto nosso a familia corresponde e nao participa de calculo nenhum.
interface FamiliaApi {
  monitor_item_id: number;
  descricao: string; // nome da familia
  markup: number; // fracao
  coleta_solicitada_em: string | null; // pedido de coleta pendente
  grupo_id: number | null; // grupo ao qual pertence (null = standalone)
  produto: { id: number; codigo_interno: string | null; descricao: string | null; estoque: number | null } | null;
  // Regua de preco (0259, DA-642): os produtos do cadastro que ESTA familia precifica, com o fator
  // (metro = 1, rolo de 100m = 100). Vazia = o botao de atualizar precos ainda nao tem em quem mexer.
  regua: { vinculo_id: number; produto_id: number; codigo_interno: string | null; descricao: string | null; unidade: string | null; fator: number; preco_custo: number | null; preco_pix: number | null }[];
  ofertas: OfertaApi[];
  total_disponivel: number;
  total_nd: number;
  melhor: {
    fornecedor_nome: string; codigo_fornecedor: string; uf: string; marca: string | null;
    qtd: number; custo_unit: number | null; fc_pix_unit: number | null;
  } | null;
}

interface GrupoApi { grupo_id: number; nome: string; ordem: number; }

interface FornecedorApi { id: number; razao_social: string | null; nome_fantasia: string | null; uf: string | null; cpf_cnpj: string | null; }

interface MonitorResp {
  cupom_pct: number;
  fator_site_pct?: number; // fator padrao da Tramontina, em % ([DA-804])
  ultima_coleta: string | null;
  total_familias: number;
  total_nd: number;
  familias: FamiliaApi[];
  grupos: GrupoApi[];
  // progresso REAL do coletor (codigo X de Y) + batimento: visto_ha_seg = ha quantos seg ele reportou.
  coletor_progresso?: { feitos: number; total: number; visto_ha_seg: number | null };
  // Ultima coleta RECUSADA pelo backend (precos preservados). null = nenhuma pendencia a mostrar.
  aviso_coleta?: { motivo: string; texto: string; em: string } | null;
}

// Status de vinculacao de um codigo no catalogo (fornecedor_catalogo).
interface CatalogoVinculo { produto_id: number; codigo_interno: string | null; descricao: string | null; }
type CatalogoStatusMap = Record<string, CatalogoVinculo | null>;

// Busca por nome na Martins (descoberta de codigo) — espelha monitor_busca.
interface BuscaResultadoItem { codigo: string; descricao: string | null; preco: number | null; uf?: string | null; }
interface BuscaApi {
  id: number;
  termo: string;
  fornecedor: string;
  status: 'pendente' | 'coletando' | 'respondida' | 'erro';
  resultado: BuscaResultadoItem[] | null;
  total_resultados: number | null;
  truncado: boolean;
  progresso_pagina: number | null;
  progresso_paginas: number | null;
  criado_em: string;
  respondido_em: string | null;
}

const m = (v: number | null | undefined, c = 2) => (v == null ? '—' : formatMoedaSemSimbolo(v, c));

// Preco digitado a mao chega em pt-BR ("134,93", "1.234,56"). Ponto e separador de MILHAR aqui, e
// nao decimal: quem digita esta copiando o numero da tela do fornecedor, do jeito que ele aparece la.
// Vazio -> null, que e a resposta legitima (limpa o preco digitado da oferta).
function parsePrecoBR(txt: string): number | null {
  const limpo = txt.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  if (!limpo.trim()) return null;
  const n = Number(limpo);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function seloUf(uf: string): CSSProperties {
  const mg = (uf || '').toUpperCase() === 'MG';
  return {
    fontFamily: 'var(--font-mono)', fontSize: 10.5, padding: '1px 7px', borderRadius: 5,
    border: '1px solid var(--panel-border)',
    background: mg ? 'rgba(147,51,234,0.12)' : 'var(--accent-soft)',
    color: mg ? '#9333ea' : 'var(--primary)',
  };
}

const num: CSSProperties = { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };

// Botao pequeno DENTRO do cabecalho da familia (que e `var(--panel-bg)`). O `btn-secundario` sai
// transparente com borda `var(--border)`, e a DA-416 mediu: no Azul Royal essa borda (#d8e3f7) e mais
// CLARA que o proprio painel (#cfdef6) — o botao vira texto solto. Aqui ele ganha superficie propria
// (a mesma da pilula do markup, ao lado) e a borda cai em `var(--page-bg)`, que contrasta com o
// painel por definicao. Letra em `var(--primary)` pra ler como acao, sem o peso de um botao solido
// repetido em 19 familias.
const supPainel: CSSProperties = {
  background: 'var(--input-bg)', borderColor: 'var(--page-bg)', color: 'var(--primary)',
};

export function MonitorPrecos() {
  const toast = useToast();
  const dialog = useDialog();
  const [dados, setDados] = useState<MonitorResp | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [cupomInput, setCupomInput] = useState('7,0');
  // Fator do site da Tramontina, em % ([DA-804]): custo do representante = a vista do site x fator.
  const [fatorInput, setFatorInput] = useState('46,28');
  const [markupEdit, setMarkupEdit] = useState<Record<number, string>>({});
  // Forms inline: "+ Adicionar codigo" por familia (itemId) e edicao de oferta (ofertaId).
  const [addCodPara, setAddCodPara] = useState<number | null>(null);
  const [editandoOferta, setEditandoOferta] = useState<number | null>(null);
  // "Mover para outra familia": o codigo escolhido e a familia onde ele esta hoje.
  const [trocarFamilia, setTrocarFamilia] = useState<{ oferta: OfertaApi; familiaId: number } | null>(null);
  // Linha marcada por clique: fundo azul-claro + moldura tracejada (a melhor oferta segue amarela,
  // com moldura solida + selo). A cor do FORNECEDOR fica so nas celulas de setas e codigo.
  const [ofertaSel, setOfertaSel] = useState<number | null>(null);
  // Foto do codigo: modal de colar (oferta aberta) + preview ampliado no hover da miniatura.
  const [fotoOferta, setFotoOferta] = useState<OfertaApi | null>(null);
  // Familia com o modal de "Atualizar precos" aberto (regua + previa + aplicar) — DA-642.
  // Qual linha de media abriu o modal: a familia + o fornecedor daquela linha (null = sem media).
  const [precosPara, setPrecosPara] = useState<{ familia: FamiliaApi; fornecedorId: number | null; fornecedorNome: string | null } | null>(null);
  const [fotoHover, setFotoHover] = useState<{ url: string; x: number; y: number } | null>(null);
  const [fornecedores, setFornecedores] = useState<FornecedorApi[]>([]);
  const [solicitando, setSolicitando] = useState<Record<number, boolean>>({});
  // Coleta por LINHA (oferta): spinner imediato no botao da linha enquanto o clique registra o pedido.
  const [solicitandoOferta, setSolicitandoOferta] = useState<Record<number, boolean>>({});
  const [salvandoCatalogo, setSalvandoCatalogo] = useState<Record<number, boolean>>({});
  const [modalFamilia, setModalFamilia] = useState(false);
  const [modalGrupo, setModalGrupo] = useState(false);
  // Grupo alvo ao abrir "Adicionar produto": abre o modal de familia com grupo_id fixo.
  const [addProdAoGrupo, setAddProdAoGrupo] = useState<number | null>(null);
  // Editar nome de grupo inline
  const [editandoGrupo, setEditandoGrupo] = useState<number | null>(null);
  const [nomeGrupoEdit, setNomeGrupoEdit] = useState('');
  // Renomear a familia inline (clique duplo no cabecalho do bloco).
  const [editandoItemNome, setEditandoItemNome] = useState<number | null>(null);
  const [nomeItemEdit, setNomeItemEdit] = useState('');
  // Vincular a familia a um produto do cadastro (opcional, migration 0199). `vincFam` = a familia
  // com o autocomplete aberto. A lista de resultados e desenhada com position:fixed medida do
  // input, porque o card da familia e `overflow: hidden` e clipa qualquer dropdown absoluto.
  const [vincFam, setVincFam] = useState<number | null>(null);
  const [vincBusca, setVincBusca] = useState('');
  const [vincResultados, setVincResultados] = useState<ProdutoBusca[]>([]);
  const vincTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vincInputRef = useRef<HTMLInputElement | null>(null);
  const [vincPos, setVincPos] = useState<PosLista | null>(null);
  // "x" do HUD de coleta: esconde o painel sem cancelar a coleta (so visual). Volta a aparecer
  // numa coleta nova (reset abaixo). A coleta segue na fila — o coletor pega quando abrir.
  const [bannerFechado, setBannerFechado] = useState(false);

  // Filtro de exibicao: mostra familias avulsas (sem grupo) e/ou os grupos. Desmarcar oculta o bloco.
  const [exibirFamilias, setExibirFamilias] = useState(() => localStorage.getItem('mp:exibir-familias') !== 'false');
  const [exibirGrupos, setExibirGrupos] = useState(() => localStorage.getItem('mp:exibir-grupos') !== 'false');
  // Foco: quando definido, exibe APENAS o item selecionado (familia standalone ou grupo-container).
  const [focoId, setFocoId] = useState<{ tipo: 'familia'; id: number } | { tipo: 'grupo'; id: number } | null>(() => {
    try { const s = localStorage.getItem('mp:foco-id'); return s ? JSON.parse(s) : null; } catch { return null; }
  });

  // Selo "Coletor online/offline": batimento leve do coletor local. null = ainda nao consultado.
  // Um por fornecedor (Martins, Tambasa): cada um tem a SUA janela de robo.
  const [coletores, setColetores] = useState<{ fornecedor: string; online: boolean }[] | null>(null);

  // BUSCA POR NOME na Martins (descoberta de codigo). Termo -> POST /busca -> polling GET /busca.
  const [buscaTermo, setBuscaTermo] = useState('');
  const [busca, setBusca] = useState<BuscaApi | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [buscaModal, setBuscaModal] = useState(false);
  // se a busca foi aberta a partir do "+ Adicionar codigo" de uma familia, mira nela ao adicionar.
  const [buscaAlvoFamilia, setBuscaAlvoFamilia] = useState<number | null>(null);
  // codigo pre-preenchido pra abrir o form "+ Adicionar codigo" de uma familia.
  const [codigoPre, setCodigoPre] = useState<Record<number, string>>({});
  const buscaIniciadaEm = useRef<number>(0);

  // O produto do lapis tambem vai pro Catalogo, que e' de onde a cotacao le ([DA-729]) — mas so
  // COMPLETA: linha do Catalogo ligada a outro produto fica como esta, e isso tem que ser dito.
  function avisarVinculoCatalogo(estado: string | null | undefined, feito: string) {
    if (estado === 'vinculado') toast.sucesso(`${feito} — produto vinculado tambem no Catalogo do Fornecedor`);
    else if (estado === 'outro_produto') toast.aviso(`${feito}. No Catalogo do Fornecedor este codigo esta ligado a OUTRO produto — nao troquei. Confira la.`);
    else if (estado === 'falhou') toast.aviso(`${feito}, mas nao consegui levar o produto ao Catalogo do Fornecedor. Salve de novo.`);
    else toast.sucesso(feito);
  }

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<MonitorResp>('/api/monitor-precos');
      setDados(r);
      setCupomInput(r.cupom_pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
      if (r.fator_site_pct != null) setFatorInput(r.fator_site_pct.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 }));
    } catch (e) {
      toast.erro('Falha ao carregar o monitor: ' + (e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [toast]);

  // Fornecedores pro seletor do form de oferta: SO os homologados no monitor (tem coletor) — hoje
  // MARTINS e TAMBASA ([DA-749]). Sem isso o dropdown puxava a lista inteira de cadastros e dava pra
  // escolher um fornecedor sem coletor (ex: PARAFIX), criando oferta que nunca coleta.
  useEffect(() => {
    void api.get<FornecedorApi[]>('/api/monitor-precos/fornecedores').then(setFornecedores).catch(() => {});
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  // F5 volta pro mesmo lugar (pedido dele, 09/10/2026). Quem rola e o .app-content, nao a janela —
  // por isso o navegador nao restaura sozinho. A posicao vai pro sessionStorage enquanto ele rola
  // (por aba: outra aba do Monitor nao herda), e volta UMA vez, depois da 1a carga desenhar a lista.
  const scrollRestaurado = useRef(false);
  useEffect(() => {
    const cont = document.querySelector('.app-content') as HTMLElement | null;
    if (!cont) return;
    let raf = 0;
    const guardar = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // So guarda depois de restaurar: a lista ainda vazia rola pra 0 e apagaria a posicao.
        if (!scrollRestaurado.current) return;
        try { sessionStorage.setItem('monitor.scroll', String(cont.scrollTop)); } catch { /* sem storage: so nao lembra */ }
      });
    };
    cont.addEventListener('scroll', guardar, { passive: true });
    return () => { cont.removeEventListener('scroll', guardar); cancelAnimationFrame(raf); };
  }, []);
  useEffect(() => {
    if (!dados || scrollRestaurado.current) return;
    let y = 0;
    try { y = Number(sessionStorage.getItem('monitor.scroll') || 0); } catch { /* idem */ }
    requestAnimationFrame(() => {
      const cont = document.querySelector('.app-content') as HTMLElement | null;
      if (cont && y > 0) cont.scrollTop = y;
      scrollRestaurado.current = true;
    });
  }, [dados]);

  // Auto-refresh sem F5: enquanto houver coleta "aguardando coletor" — de FAMILIA (HUD do topo) ou de
  // LINHA (botao da oferta) — consulta o backend a cada 4s e a tela se atualiza sozinha quando o preco chega.
  // temPendente (so familia) segue governando o HUD/loteRef; a oferta pendente entra so no polling.
  const temPendente = !!dados?.familias?.some((f) => f.coleta_solicitada_em);
  const temPendenteOferta = !!dados?.familias?.some((f) => f.ofertas.some((o) => o.coleta_solicitada_em));
  const temQualquerPendente = temPendente || temPendenteOferta;
  useEffect(() => {
    if (!temQualquerPendente) return;
    const id = setInterval(() => { void carregar(); }, 4000);
    return () => clearInterval(id);
  }, [temQualquerPendente, carregar]);
  // Releituras aos 2s, 5s e 12s depois que a coleta termina: a foto que o robo traz e' baixada pelo
  // servidor DEPOIS de gravar o preco ([DA-803]), e o polling acima para no instante em que o preco
  // chega. Sem isto a linha nova ficava com o quadro vazio ate o F5 (08/10/2026, 081818 da Tambasa:
  // preco 19:43:51, foto 19:43:52, tela ja parada). Era UMA releitura aos 6s, e era tarde: 09/10, oferta
  // 335, foto gravada 1s depois do preco e ele clicou de novo aos 8s, achando que nao tinha vindo. A
  // ultima cobre o download lento (o servidor desiste aos 15s). So na transicao pendente -> nada.
  const pendenteAntesRef = useRef(false);
  useEffect(() => {
    const antes = pendenteAntesRef.current;
    pendenteAntesRef.current = temQualquerPendente;
    if (!antes || temQualquerPendente) return;
    const ts = [2000, 5000, 12000].map((ms) => setTimeout(() => { void carregar(); }, ms));
    return () => ts.forEach(clearTimeout);
  }, [temQualquerPendente, carregar]);

  // some a coleta -> reabilita o HUD pra proxima (quem fechou no "x" volta a ver na coleta seguinte).
  useEffect(() => { if (!temQualquerPendente) setBannerFechado(false); }, [temQualquerPendente]);
  useEffect(() => { localStorage.setItem('mp:exibir-familias', String(exibirFamilias)); }, [exibirFamilias]);
  useEffect(() => { localStorage.setItem('mp:exibir-grupos', String(exibirGrupos)); }, [exibirGrupos]);
  useEffect(() => { if (focoId == null) localStorage.removeItem('mp:foco-id'); else localStorage.setItem('mp:foco-id', JSON.stringify(focoId)); }, [focoId]);

  // Batimento do coletor pro selo de status — roda SEMPRE (mesmo ocioso), leve (so o heartbeat em
  // memoria, nao toca o banco). O backend considera vivo se o coletor pingou nos ultimos 12s.
  useEffect(() => {
    let vivo = true;
    const ping = async () => {
      try {
        const r = await api.get<{ online: boolean; coletores?: { fornecedor: string; online: boolean }[] }>('/api/monitor-precos/coletor-status');
        if (vivo) setColetores(r.coletores ?? [{ fornecedor: 'Martins', online: !!r.online }]);
      } catch { if (vivo) setColetores(null); }
    };
    void ping();
    const id = setInterval(() => { void ping(); }, 6000);
    return () => { vivo = false; clearInterval(id); };
  }, []);

  // "Relogio" que avanca o cronometro da espera de coleta por familia (estilo ProgressoBusca da
  // busca por nome). So roda enquanto ha familia aguardando coletor. O tempo se ancora em
  // coleta_solicitada_em (do backend) — sobrevive a F5 e nao depende de quando a aba abriu.
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!temQualquerPendente) return;
    setAgora(Date.now());
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, [temQualquerPendente]);

  // Lote de coleta em curso: total = denominador REAL do % (vem do clique, nao adivinhado) e
  // inicioMs = quando comecou. Geral seta total = nº de familias enfileiradas; por familia seta 1
  // (ou cresce). Zera quando acaba. Fallback (F5 no meio): reconstroi do que esta pendente.
  const loteRef = useRef<{ total: number; inicioMs: number }>({ total: 0, inicioMs: 0 });
  // pendentes = familias pedidas (Atualizar do cabecalho) + ofertas pedidas por LINHA (Atualizar da
  // linha, sem a familia inteira pedida). inicioMs ancora SEMPRE no relogio do CLIENTE (Date.now() na
  // 1a deteccao) — nunca no timestamp do banco (VPS) que, misturado com Date.now(), trava a barra em 0
  // pelo skew de relogio (regra do CLAUDE.md). Vale pros DOIS gatilhos, familia e linha.
  const pendentesFamAgora = dados?.familias?.filter((f) => f.coleta_solicitada_em).length ?? 0;
  const pendentesOfAgora = dados?.familias?.reduce((n, f) => n + f.ofertas.filter((o) => o.coleta_solicitada_em && !f.coleta_solicitada_em).length, 0) ?? 0;
  const pendentesAgora = pendentesFamAgora + pendentesOfAgora;
  if (!temQualquerPendente) {
    loteRef.current = { total: 0, inicioMs: 0 };
  } else {
    if (loteRef.current.inicioMs === 0) loteRef.current = { total: pendentesAgora, inicioMs: Date.now() };
    else if (pendentesAgora > loteRef.current.total) loteRef.current.total = pendentesAgora; // cresce se clicar mais
  }

  // Apos abrir o form "+ Adicionar codigo" (pelo botao da familia OU vindo do modal de
  // busca por nome — que fecha o modal e abre o form la embaixo), rola ele pra dentro da
  // tela e da um realce. Senao, vindo da busca, parecia que o clique "so fechava o modal".
  useEffect(() => {
    if (addCodPara == null) return;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`monitor-addcod-${addCodPara}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove('fdc-destaque');
      void el.offsetWidth; // reinicia a animacao se ja estava marcado
      el.classList.add('fdc-destaque');
    });
    return () => cancelAnimationFrame(raf);
  }, [addCodPara]);

  // Arrastar pra reordenar as ofertas DENTRO de uma familia (mesmo controle do orcamento).
  const [dragFam, setDragFam] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  function limparDrag() { setDragFam(null); setDragIdx(null); setDragOverIdx(null); }

  async function soltarOferta(familiaId: number, fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return;
    const fam = dados?.familias.find((f) => f.monitor_item_id === familiaId);
    if (!fam) return;
    const novos = [...fam.ofertas];
    const [movido] = novos.splice(fromIdx, 1);
    if (!movido) return;
    const destino = fromIdx < toIdx ? toIdx - 1 : toIdx;
    novos.splice(destino, 0, movido);
    const ids = novos.map((o) => o.oferta_id);
    // otimista: reordena na tela na hora; persiste depois (reverte recarregando se falhar).
    setDados((d) => d ? {
      ...d,
      familias: d.familias.map((f) => f.monitor_item_id === familiaId ? { ...f, ofertas: novos } : f),
    } : d);
    try {
      await api.put('/api/monitor-precos/ofertas/reordenar', { monitor_item_id: familiaId, ids });
    } catch (e) {
      toast.erro('Falha ao salvar a ordem: ' + (e as Error).message);
      await carregar();
    }
  }

  // Reposicionar UMA casa pra cima/baixo pelos botoes ▲/▼ (alternativa ao drag, que trava em
  // algumas telas — convencao "setas paralelas ao arrastar"). idx = posicao na lista da familia.
  async function moverOferta(familiaId: number, idx: number, delta: number) {
    const fam = dados?.familias.find((f) => f.monitor_item_id === familiaId);
    if (!fam) return;
    const to = idx + delta;
    if (to < 0 || to >= fam.ofertas.length) return;
    const novos = [...fam.ofertas];
    const [movido] = novos.splice(idx, 1);
    if (!movido) return;
    novos.splice(to, 0, movido);
    const ids = novos.map((o) => o.oferta_id);
    // otimista: reordena na tela na hora; persiste pelo mesmo cano do drop (reverte se falhar).
    setDados((d) => d ? {
      ...d,
      familias: d.familias.map((f) => f.monitor_item_id === familiaId ? { ...f, ofertas: novos } : f),
    } : d);
    try {
      await api.put('/api/monitor-precos/ofertas/reordenar', { monitor_item_id: familiaId, ids });
    } catch (e) {
      toast.erro('Falha ao salvar a ordem: ' + (e as Error).message);
      await carregar();
    }
  }

  // Reposicionar um bloco STANDALONE (fora de contêiner) uma casa pra cima/baixo pelas setas ▲/▼.
  // subset = a lista VISIVEL da secao (so Familias-com-SKU ou so Grupos-sem-SKU): troca a posicao
  // GLOBAL dos dois blocos vizinhos DENTRO do subset, preservando a ordem dos blocos da outra
  // categoria. Persiste ordem = 1..N no backend sobre TODO o standalone (sort global consistente).
  async function moverBlocoStandalone(subset: FamiliaApi[], idx: number, delta: number) {
    if (!dados) return;
    const to = idx + delta;
    if (to < 0 || to >= subset.length) return;
    const item = subset[idx];
    const alvo = subset[to];
    if (!item || !alvo) return;
    const standalone = dados.familias.filter((f) => f.grupo_id == null);
    const arr = [...standalone];
    const i = arr.findIndex((f) => f.monitor_item_id === item.monitor_item_id);
    const j = arr.findIndex((f) => f.monitor_item_id === alvo.monitor_item_id);
    if (i < 0 || j < 0) return;
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
    const ids = arr.map((f) => f.monitor_item_id);
    setDados((d) => d ? {
      ...d,
      familias: [
        ...d.familias.filter((f) => f.grupo_id != null),
        ...arr,
      ],
    } : d);
    try {
      await api.put('/api/monitor-precos/itens/reordenar', { ids });
    } catch (e) {
      toast.erro('Falha ao salvar a ordem: ' + (e as Error).message);
      await carregar();
    }
  }

  // Move familia DENTRO de um grupo uma casa pra cima/baixo.
  async function moverFamiliaNoGrupo(grupoId: number, idx: number, delta: number) {
    if (!dados) return;
    const membros = dados.familias.filter((f) => f.grupo_id === grupoId);
    const to = idx + delta;
    if (to < 0 || to >= membros.length) return;
    const novos = [...membros];
    const [movido] = novos.splice(idx, 1);
    if (!movido) return;
    novos.splice(to, 0, movido);
    const ids = novos.map((f) => f.monitor_item_id);
    setDados((d) => d ? {
      ...d,
      familias: d.familias.map((f) => {
        const novoIdx = ids.indexOf(f.monitor_item_id);
        return novoIdx >= 0 ? novos[novoIdx]! : f;
      }),
    } : d);
    try {
      await api.put('/api/monitor-precos/itens/reordenar', { ids });
    } catch (e) {
      toast.erro('Falha ao salvar a ordem: ' + (e as Error).message);
      await carregar();
    }
  }

  // Move GRUPO uma casa pra cima/baixo.
  async function moverGrupo(idx: number, delta: number) {
    if (!dados) return;
    const to = idx + delta;
    if (to < 0 || to >= dados.grupos.length) return;
    const novos = [...dados.grupos];
    const [movido] = novos.splice(idx, 1);
    if (!movido) return;
    novos.splice(to, 0, movido);
    const ids = novos.map((g) => g.grupo_id);
    setDados((d) => d ? { ...d, grupos: novos } : d);
    try {
      await api.put('/api/monitor-precos/grupos/reordenar', { ids });
    } catch (e) {
      toast.erro('Falha ao salvar a ordem: ' + (e as Error).message);
      await carregar();
    }
  }

  async function salvarNomeGrupo(grupoId: number) {
    const nome = nomeGrupoEdit.trim();
    if (!nome) { setEditandoGrupo(null); return; }
    try {
      await api.put(`/api/monitor-precos/grupos/${grupoId}`, { nome });
      setEditandoGrupo(null);
      await carregar();
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function excluirGrupo(grupoId: number, nomeGrupo: string) {
    if (!(await dialog.confirmar(`Excluir o grupo "${nomeGrupo}"?\n\nAs familias dentro dele viram standalone (nao sao apagadas).`, { perigoso: true }))) return;
    try {
      await api.delete(`/api/monitor-precos/grupos/${grupoId}`);
      await carregar();
      toast.sucesso('Grupo excluido');
    } catch (e) { toast.erro((e as Error).message); }
  }

  // Abre a edicao inline do nome (pelo lapis de 1 clique ou pelo clique duplo no proprio nome).
  function abrirEdicaoNome(f: FamiliaApi) {
    setEditandoItemNome(f.monitor_item_id);
    setNomeItemEdit((f.descricao || '').toUpperCase());
  }

  // Renomeia a familia. O nome e o unico rotulo que existe — vazio so cancela a edicao (nao ha
  // produto pra onde cair).
  async function salvarNomeItem(itemId: number) {
    const nome = nomeItemEdit.trim();
    if (!nome) { setEditandoItemNome(null); return; }
    try {
      await api.put(`/api/monitor-precos/itens/${itemId}/nome`, { nome });
      setEditandoItemNome(null);
      await carregar();
    } catch (e) { toast.erro((e as Error).message); }
  }

  // ---- Vinculo da familia com um produto do cadastro (opcional) ----
  function abrirVinculo(f: FamiliaApi) {
    setVincFam(f.monitor_item_id);
    // Abre VAZIO mesmo com produto ja vinculado: o campo e uma busca, e o rotulo do vinculo atual
    // segue visivel ao lado. Pre-preencher com "0296 — CABO..." so daria um texto que nao casa
    // com nada pra apagar antes de digitar.
    setVincBusca('');
    setVincResultados([]);
  }
  function fecharVinculo() {
    setVincFam(null);
    setVincBusca('');
    setVincResultados([]);
  }

  useEffect(() => {
    if (vincTimer.current) clearTimeout(vincTimer.current);
    if (vincFam == null || vincBusca.trim().length < 2) { setVincResultados([]); return; }
    vincTimer.current = setTimeout(async () => {
      try {
        const r = await api.get<ProdutoBusca[]>('/api/produtos', { query: { q: vincBusca, com_inativos: '1' } });
        setVincResultados((r || []).slice(0, MAX_RESULTADOS_PRODUTO));
      } catch { setVincResultados([]); }
    }, 250);
    return () => { if (vincTimer.current) clearTimeout(vincTimer.current); };
  }, [vincBusca, vincFam]);

  // Ancora a lista no input (position: fixed). Remede no scroll/resize senao ela descola ao rolar.
  useEffect(() => {
    if (vincFam == null || vincResultados.length === 0) { setVincPos(null); return; }
    const medir = () => {
      const el = vincInputRef.current;
      if (!el) return;
      setVincPos(posicaoLista(el.getBoundingClientRect()));
    };
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => { window.removeEventListener('scroll', medir, true); window.removeEventListener('resize', medir); };
  }, [vincFam, vincResultados.length]);

  async function salvarVinculo(itemId: number, produtoId: number | null) {
    try {
      await api.put(`/api/monitor-precos/itens/${itemId}/produto`, { produto_id: produtoId });
      fecharVinculo();
      await carregar();
      toast.sucesso(produtoId ? 'Produto vinculado a familia.' : 'Vinculo removido.');
    } catch (e) { toast.erro((e as Error).message); }
  }
  function escolherVinculo(p: ProdutoBusca) {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    if (vincFam != null) void salvarVinculo(vincFam, p.id);
  }
  const tecladoVinculo = useListaTeclado(vincResultados, escolherVinculo);

  async function excluirFamilia(itemId: number, nome: string) {
    if (!(await dialog.confirmar(`Excluir a familia "${nome}"?\n\nOs codigos monitorados dela saem junto.`, { perigoso: true }))) return;
    try {
      await api.delete(`/api/monitor-precos/itens/${itemId}`);
      await carregar();
      toast.sucesso('Familia excluida');
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function salvarCupom() {
    const pct = Number(cupomInput.replace(',', '.'));
    if (!Number.isFinite(pct)) { toast.erro('Cupom invalido'); return; }
    try {
      await api.put('/api/monitor-precos/cupom', { cupom_pct: pct });
      await carregar();
      toast.sucesso('Cupom atualizado');
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function salvarFator() {
    const pct = lerPct(fatorInput);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) { toast.erro('Fator invalido — informe entre 0 e 100%'); return; }
    if (dados?.fator_site_pct != null && Math.abs(dados.fator_site_pct - pct) < 0.0005) return;
    try {
      await api.put('/api/monitor-precos/fator-site', { fator_pct: pct });
      await carregar();
      toast.sucesso('Fator da Tramontina atualizado');
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function salvarMarkup(itemId: number) {
    const raw = markupEdit[itemId];
    if (raw == null) return;
    const pct = Number(String(raw).replace(',', '.'));
    if (!Number.isFinite(pct)) { toast.erro('Markup invalido'); return; }
    try {
      await api.put(`/api/monitor-precos/itens/${itemId}/markup`, { markup: pct / 100 });
      setMarkupEdit((s) => { const c = { ...s }; delete c[itemId]; return c; });
      await carregar();
    } catch (e) { toast.erro((e as Error).message); }
  }

  // Botao "Atualizar" POR familia: marca o pedido de coleta. O coletor --watch (rodando local
  // na sessao logada do Ruan) pega o pedido, coleta so esse item e o backend limpa o flag.
  async function solicitarColeta(itemId: number) {
    setSolicitando((s) => ({ ...s, [itemId]: true }));
    try {
      const r = await api.post<{ ok: boolean; enfileirado: boolean; fornecedores_offline?: string[] }>(`/api/monitor-precos/${itemId}/solicitar-coleta`);
      // Coletor fechado: o backend NAO enfileirou. So a mensagem (sem HUD de progresso falso).
      if (r && r.enfileirado === false) { await avisarColetorFechado(r.fornecedores_offline); return; }
      avisarParteDeFora(r?.fornecedores_offline);
      // alimenta o lote: se nao havia coleta rodando, comeca 1; senao soma (clicou em outra familia).
      if (loteRef.current.total === 0) loteRef.current = { total: 1, inicioMs: Date.now() };
      else loteRef.current.total += 1;
      setBannerFechado(false); // coleta nova reabre o HUD
      await carregar();
      toast.mostrar('Coleta solicitada — aguardando o coletor', 'info');
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setSolicitando((s) => { const c = { ...s }; delete c[itemId]; return c; });
    }
  }

  // Coletor (janela "Atualizar Precos <fornecedor>") fechado: nada foi enfileirado. Mensagem clara, em
  // tom de AVISO (ambar, daltonismo-safe — nao "Erro" vermelho) e SEM HUD por cima (nada esta coletando).
  // Cada fornecedor tem o SEU robo; a mensagem nomeia a janela que falta abrir.
  async function avisarColetorFechado(offline?: string[]) {
    const lista = offline && offline.length ? offline : ['Martins'];
    const janelas = lista.map((n) => `"Atualizar Precos ${n}"`).join(' e ');
    const plural = lista.length > 1;
    await dialog.alerta(
      `${plural ? 'As janelas' : 'A janela'} ${janelas} ${plural ? 'estao fechadas' : 'esta fechada'} no seu computador.\n\n` +
      `Abra ${plural ? 'os atalhos' : 'o atalho'} ${janelas} na Area de Trabalho e clique em Atualizar de novo.\n\n` +
      'Se voce acabou de abrir a janela, espere uns segundos antes de tentar.',
      { titulo: 'Coletor fechado', tipo: 'aviso' },
    );
  }

  // Enfileirou, mas uma parte das linhas ficou de fora porque o robo daquele fornecedor esta fechado.
  // Sem o aviso a tela diria "coleta solicitada" e as linhas da Tambasa ficariam com o preco velho,
  // caladas.
  function avisarParteDeFora(offline?: string[]) {
    if (!offline || !offline.length) return;
    toast.aviso(`As linhas da ${offline.join(' e da ')} ficaram de fora: a janela "Atualizar Precos ${offline.join('" / "Atualizar Precos ')}" esta fechada.`);
  }

  // Cancela o pedido de coleta de UMA familia (libera o botao quando o coletor nao esta aberto).
  async function cancelarColeta(itemId: number) {
    try {
      await api.post(`/api/monitor-precos/${itemId}/cancelar-coleta`);
      if (loteRef.current.total > 0) loteRef.current.total -= 1;
      await carregar();
      toast.mostrar('Coleta cancelada', 'info');
    } catch (e) { toast.erro((e as Error).message); }
  }

  // Cancela TODOS os pedidos pendentes (Cancelar do HUD geral).
  async function cancelarTodas() {
    try {
      await api.post('/api/monitor-precos/cancelar-coleta-todas');
      loteRef.current = { total: 0, inicioMs: 0 };
      await carregar();
      toast.mostrar('Coleta cancelada', 'info');
    } catch (e) { toast.erro((e as Error).message); }
  }

  // Botao "Atualizar" POR LINHA (oferta): coleta so aquele codigo, sem recoletar a familia. Util pra
  // conferir o preco de um codigo recem-inserido. Mesmo coletor --watch; o backend limpa o flag ao gravar.
  // Caixa de DIFAL da linha ([DA-727]): grava na hora e recarrega, porque o custo, o armazem mais
  // barato e a media da familia dependem dela.
  const [salvandoDifal, setSalvandoDifal] = useState<Record<number, boolean>>({});
  async function salvarDifal(ofertaId: number, valor: string) {
    setSalvandoDifal((s) => ({ ...s, [ofertaId]: true }));
    try {
      await api.put(`/api/monitor-precos/ofertas/${ofertaId}`, { difal_pct: valor === '' ? null : Number(valor) });
      await carregar();
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setSalvandoDifal((s) => { const c = { ...s }; delete c[ofertaId]; return c; });
    }
  }

  async function solicitarColetaOferta(ofertaId: number) {
    setSolicitandoOferta((s) => ({ ...s, [ofertaId]: true }));
    try {
      const r = await api.post<{ ok: boolean; enfileirado: boolean; fornecedores_offline?: string[] }>(`/api/monitor-precos/ofertas/${ofertaId}/solicitar-coleta`);
      if (r && r.enfileirado === false) { await avisarColetorFechado(r.fornecedores_offline); return; }
      await carregar();
      toast.mostrar('Coleta do codigo solicitada — aguardando o coletor', 'info');
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setSolicitandoOferta((s) => { const c = { ...s }; delete c[ofertaId]; return c; });
    }
  }

  // A IDA Monitor -> Catalogo do Fornecedor (DA-370): um clique grava o codigo no catalogo (empresa +
  // filial pela UF, preco de embalagem cru, marca, qtd) e JA vincula a oferta pra o preco seguir fresco.
  async function salvarNoCatalogo(ofertaId: number) {
    setSalvandoCatalogo((s) => ({ ...s, [ofertaId]: true }));
    try {
      const r = await api.post<{ ok: boolean; ja_vinculado: boolean; fornecedor_nome?: string; uf?: string; com_produto?: boolean }>(
        `/api/monitor-precos/ofertas/${ofertaId}/salvar-catalogo`,
      );
      await carregar();
      if (r?.ja_vinculado) {
        toast.mostrar('Este codigo ja esta no Catalogo do Fornecedor', 'info');
      } else if (r?.com_produto === false) {
        // codigo-so (produto novo, sem cadastro): o aviso NAO promete que trouxe o produto pra loja.
        toast.sucesso('Salvo no Catalogo como codigo (sem produto — a nota vincula depois) e vinculado ao Monitor');
      } else {
        const onde = r?.fornecedor_nome ? ` (${r.fornecedor_nome}${r.uf ? ' ' + r.uf : ''})` : '';
        toast.sucesso(`Salvo no Catalogo${onde} e vinculado ao Monitor`);
      }
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setSalvandoCatalogo((s) => { const c = { ...s }; delete c[ofertaId]; return c; });
    }
  }

  async function cancelarColetaOferta(ofertaId: number) {
    try {
      await api.post(`/api/monitor-precos/ofertas/${ofertaId}/cancelar-coleta`);
      await carregar();
      toast.mostrar('Coleta cancelada', 'info');
    } catch (e) { toast.erro((e as Error).message); }
  }

  async function removerOferta(ofertaId: number) {
    if (!(await dialog.confirmar('Remover esta oferta?', { perigoso: true }))) return;
    try {
      await api.delete(`/api/monitor-precos/ofertas/${ofertaId}`);
      await carregar();
      toast.sucesso('Oferta removida');
    } catch (e) { toast.erro((e as Error).message); }
  }

  // "Atualizar precos" (global) = enfileira a coleta de TODAS as familias de uma vez. O coletor
  // --watch (janela local logada) processa uma a uma e o backend limpa o flag conforme cada coleta
  // chega — o HUD do topo (ProgressoCoreSync) mostra o progresso real (familias concluidas / total).
  async function atualizar() {
    setAtualizando(true);
    try {
      const r = await api.post<{ ok: boolean; total: number; enfileirado: boolean; fornecedores_offline?: string[] }>('/api/monitor-precos/solicitar-coleta-todas');
      // Coletor fechado: nada foi enfileirado -> so a mensagem, sem HUD.
      if (r && r.enfileirado === false) { await avisarColetorFechado(r.fornecedores_offline); return; }
      avisarParteDeFora(r?.fornecedores_offline);
      // denominador REAL do progresso: o total que o backend enfileirou agora (nao adivinhado).
      loteRef.current = { total: r.total, inicioMs: Date.now() };
      setBannerFechado(false); // coleta nova reabre o HUD (caso tenha sido fechado no "x")
      await carregar();
      toast.mostrar(`Coleta solicitada — ${r.total} familias na fila`, 'info');
    } catch (e) {
      toast.erro((e as Error).message);
    } finally {
      setAtualizando(false);
    }
  }

  // BUSCA POR NOME: dispara a busca, abre o painel e comeca o polling. O coletor --watch (mesma
  // janela) pega a pendente, raspa a lista de cards e responde — a tela atualiza sozinha.
  const carregarBusca = useCallback(async () => {
    try {
      const r = await api.get<BuscaApi | null>('/api/monitor-precos/busca');
      setBusca(r);
      return r;
    } catch { return null; }
  }, []);

  async function dispararBusca(alvoFamilia: number | null = null) {
    const termo = buscaTermo.trim();
    if (termo.length < 2) { toast.erro('Digite ao menos 2 caracteres'); return; }
    setBuscando(true);
    setBuscaAlvoFamilia(alvoFamilia);
    try {
      await api.post('/api/monitor-precos/busca', { termo });
      buscaIniciadaEm.current = Date.now();
      setBuscaModal(true);
      await carregarBusca();
    } catch (e) { toast.erro((e as Error).message); }
    finally { setBuscando(false); }
  }

  // Polling enquanto a busca estiver em andamento — 'pendente' (ninguem pegou) OU 'coletando'
  // (coletor varrendo as paginas). So para quando vira 'respondida'/'erro'.
  const buscaEmAndamento = busca?.status === 'pendente' || busca?.status === 'coletando';
  useEffect(() => {
    if (!buscaEmAndamento) return;
    const id = setInterval(() => { void carregarBusca(); }, 3500);
    return () => clearInterval(id);
  }, [buscaEmAndamento, carregarBusca]);

  if (carregando) return <div style={{ padding: 40 }}>Carregando o monitor...</div>;
  if (!dados) return <div style={{ padding: 40 }}>Sem dados.</div>;

  const coletaFmt = dados.ultima_coleta
    ? new Date(dados.ultima_coleta).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;

  // Card de UMA familia — chamado tanto dentro de grupos quanto para standalone.
  // famTotal = total de familias no escopo (grupo ou standalone), usado para desabilitar setas.
  const renderFamCard = (f: FamiliaApi, famIdx: number, famTotal: number, onUp: () => void, onDown: () => void) => {
    const markupVal = markupEdit[f.monitor_item_id] ?? String(Math.round(f.markup * 100));
    const temMg = f.ofertas.some((o) => (o.uf || '').toUpperCase() === 'MG');
    // o cupom so entra na conta das linhas da Martins ([DA-750]) — a legenda so o cita se houver uma
    const temMartins = f.ofertas.some((o) => (o.fornecedor_nome || '').toUpperCase().includes('MARTINS'));
    const temSite = f.ofertas.some((o) => o.preco_de_site);
    const ultimaColetaFam = f.ofertas.reduce<string | null>((max, o) => (o.coletado_em && (!max || o.coletado_em > max)) ? o.coletado_em : max, null);
    const coletaFamFmt = ultimaColetaFam
      ? new Date(ultimaColetaFam).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
      : null;
    // Fornecedor da familia (o badge no cabecalho): codigo novo herda dele. So se a familia tem
    // um unico fornecedor; misturada (raro) ou vazia -> undefined, e o form cai no default Martins.
    const fornsFam = [...new Set(f.ofertas.map((o) => o.fornecedor_id))];
    const fornecedorPadraoFam = fornsFam.length === 1 ? fornsFam[0] : undefined;
    return (
      <details
        key={f.monitor_item_id}
        className="fam-monitor"
        data-item-id={f.monitor_item_id}
        open
        style={{ background: 'var(--panel-bg)', border: '1px solid var(--panel-border)', borderRadius: 11, marginBottom: 7, overflow: 'hidden' }}
      >
        {/* Clicar no cabecalho NAO recolhe (pedido dele, 09/10/2026: clicava no nome e a familia
            sumia). Quem recolhe e o botao -/+ ao lado do nome. Os controles do cabecalho ja param a
            propagacao, entao este preventDefault so pega o clique "solto" no cabecalho. */}
        <summary onClick={(e) => e.preventDefault()}
          style={{ listStyle: 'none', cursor: 'default', padding: '10px 14px', display: 'grid', gridTemplateColumns: 'auto 1fr 96px auto', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'inline-flex', flexDirection: 'column', justifySelf: 'start' }} onClick={(e) => e.preventDefault()}>
            <button type="button" title="Mover bloco para cima"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUp(); }}
              disabled={famIdx === 0}
              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'var(--primary)', cursor: famIdx === 0 ? 'default' : 'pointer', opacity: famIdx === 0 ? 0.25 : 1 }}>
              <ChevronUp size={18} />
            </button>
            <button type="button" title="Mover bloco para baixo"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDown(); }}
              disabled={famIdx === famTotal - 1}
              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'var(--primary)', cursor: famIdx === famTotal - 1 ? 'default' : 'pointer', opacity: famIdx === famTotal - 1 ? 0.25 : 1 }}>
              <ChevronDown size={18} />
            </button>
          </span>
          <span>
            {/* O sinal acompanha o estado pelo CSS (.fam-monitor[open]), entao o "expandir/recolher
                todas" do topo — que mexe no `open` direto — tambem troca o sinal. */}
            <button type="button" className="fam-sinal bg-blue-600 text-white hover:bg-blue-700"
              title="Recolher / expandir esta familia"
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                const d = (e.currentTarget as HTMLElement).closest('details') as HTMLDetailsElement | null;
                if (d) d.open = !d.open;
              }}
              style={{ border: 'none', borderRadius: 5, width: 22, height: 22, marginRight: 8, verticalAlign: 'middle', cursor: 'pointer', fontWeight: 700, fontSize: 16, lineHeight: '22px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} />
            {editandoItemNome === f.monitor_item_id ? (
              // UpperInput, nao <input>: o CSS global so PINTA o campo de maiusculo — quem grava
              // maiusculo de verdade e ele (senao o nome vai pro banco como foi digitado).
              <UpperInput
                value={nomeItemEdit}
                onChange={setNomeItemEdit}
                onBlur={() => void salvarNomeItem(f.monitor_item_id)}
                onKeyDown={(e) => { if (e.key === 'Enter') void salvarNomeItem(f.monitor_item_id); if (e.key === 'Escape') setEditandoItemNome(null); }}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                autoFocus
                style={{ fontWeight: 700, fontSize: 13, background: 'var(--input-bg)', border: '1px solid var(--panel-border)', borderRadius: 6, padding: '2px 8px', width: 426, maxWidth: '100%', '--input-fg': '#000' } as CSSProperties}
              />
            ) : (
              <>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#000', cursor: 'text' }}
                  onDoubleClick={(e) => { e.preventDefault(); abrirEdicaoNome(f); }}
                  title="Clique duplo para renomear">
                  {f.descricao || '(sem nome)'}
                </span>
                {/* Botao de UM clique ao lado do dblclick — a velocidade de duplo clique do Windows e
                    hostil e o dblclick sozinho so acaba selecionando o texto. */}
                <button type="button" title="Renomear a familia"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); abrirEdicaoNome(f); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #9ca3af)', padding: '0 4px', marginLeft: 2, verticalAlign: 'middle', lineHeight: 0 }}>
                  <Pencil size={13} />
                </button>
              </>
            )}
            {/* Sem selo de fornecedor no cabecalho: a origem de cada codigo ja aparece na LINHA (coluna
                UF), e o selo do bloco ainda mentia — dizia "MARTINS GO" numa familia com ofertas MG.
                Quando entrar um 2o fornecedor (Tambasa), ele tem que aparecer por linha, nao aqui. */}
            {f.grupo_id == null && (
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', color: '#000', whiteSpace: 'nowrap', marginLeft: 10, verticalAlign: 'middle' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={focoId?.tipo === 'familia' && focoId.id === f.monitor_item_id}
                  style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                  onChange={(e) => { e.stopPropagation(); setFocoId(e.target.checked ? { tipo: 'familia' as const, id: f.monitor_item_id } : null); }}
                />
                Ocultar os demais
              </label>
            )}
            <br />
            <span style={{ color: '#000', fontSize: 13, ...num }}>
              {f.total_disponivel} {f.total_disponivel === 1 ? 'opcao' : 'opcoes'}{f.total_nd ? ` · ${f.total_nd} ND` : ''}
              {coletaFamFmt
                ? <> · atualizado <b style={{ fontWeight: 600 }}>{coletaFamFmt}</b></>
                : <> · <span style={{ fontStyle: 'italic' }}>sem coleta ainda</span></>}
            </span>
            <br />
            {/* Produto do cadastro que esta familia representa (opcional, migration 0199). Fica na
                linha de dados da familia, nao na faixa de botoes: a descricao e longa e o cabecalho
                ja e um grid de 4 colunas. Sem vinculo o botao APARECE mesmo assim — vinculo que so
                se descobre no hover nao existe pra quem olha a tela. */}
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 13, color: '#000', whiteSpace: 'nowrap' }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              {vincFam === f.monitor_item_id ? (
                <>
                  <Link2 size={13} style={{ flexShrink: 0 }} />
                  <input
                    ref={vincInputRef}
                    value={vincBusca}
                    onChange={(e) => setVincBusca(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); fecharVinculo(); return; } tecladoVinculo.onKeyDown(e); }}
                    placeholder="Codigo ou descricao do produto…"
                    autoFocus
                    style={{ width: 300, fontSize: 13, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--panel-border)', '--input-fg': '#000' } as CSSProperties}
                  />
                  <button type="button" className="btn-secundario" onClick={fecharVinculo}
                    style={{ fontSize: 13, padding: '3px 9px', ...supPainel, color: '#000' }}>
                    Cancelar
                  </button>
                </>
              ) : f.produto ? (
                <>
                  <Link2 size={13} style={{ flexShrink: 0 }} />
                  <span style={{ ...num, fontWeight: 700 }}>{f.produto.codigo_interno || '—'}</span>
                  <span>— {f.produto.descricao || '(sem descricao)'}</span>
                  <button type="button" title="Trocar o produto vinculado"
                    onClick={() => abrirVinculo(f)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #9ca3af)', padding: '0 3px', lineHeight: 0 }}>
                    <Pencil size={12} />
                  </button>
                  <button type="button" title="Desvincular o produto desta familia"
                    onClick={() => void salvarVinculo(f.monitor_item_id, null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '0 3px', lineHeight: 0 }}>
                    <X size={12} />
                  </button>
                  {f.produto.estoque != null && (
                    <span style={{ ...num, fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginLeft: 6 }}
                      title="Quantidade em estoque do produto vinculado">
                      Estoque: <span style={{ fontSize: 14, fontWeight: 700 }}>{formatQtd(f.produto.estoque)}</span>
                    </span>
                  )}
                </>
              ) : (
                <button type="button" className="btn-secundario"
                  onClick={() => abrirVinculo(f)}
                  title="Apontar esta familia para um produto do cadastro"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, padding: '3px 9px', whiteSpace: 'nowrap', ...supPainel, color: '#000' }}>
                  <Link2 size={12} /> Vincular produto
                </button>
              )}
            </span>
          </span>
          <span
            style={{ justifySelf: 'start', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--input-bg)', border: '1px solid var(--panel-border)', borderRadius: 999, padding: '3px 9px', fontSize: 13, color: '#000', whiteSpace: 'nowrap' }}
            onClick={(e) => e.preventDefault()}
          >
            markup
            <input
              value={markupVal}
              onChange={(e) => setMarkupEdit((s) => ({ ...s, [f.monitor_item_id]: e.target.value }))}
              onBlur={() => salvarMarkup(f.monitor_item_id)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 36, ...num, fontSize: 13, textAlign: 'right', padding: '2px 5px', '--input-fg': '#000' } as CSSProperties}
              inputMode="decimal"
            />%
          </span>
          <span style={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 8 }} onClick={(e) => e.preventDefault()}>
            {f.coleta_solicitada_em && !solicitando[f.monitor_item_id] ? (
              <button type="button" className="btn-secundario"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelarColeta(f.monitor_item_id); }}
                title="Cancelar a coleta pendente desta familia"
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, padding: '4px 10px', color: '#000', borderColor: '#d97706' }}>
                <X size={13} /> Cancelar
              </button>
            ) : (
              <button type="button" className="btn-secundario"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); solicitarColeta(f.monitor_item_id); }}
                disabled={!!solicitando[f.monitor_item_id]}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, padding: '4px 10px', color: '#000' }}>
                <RefreshCw size={13} className={solicitando[f.monitor_item_id] ? 'animate-spin' : ''} /> Atualizar
              </button>
            )}
            <button type="button" className="btn-primario"
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                const d = e.currentTarget.closest('details') as HTMLDetailsElement | null;
                if (d) d.open = true;
                setAddCodPara(f.monitor_item_id); setEditandoOferta(null);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, padding: '4px 10px' }}>
              <Plus size={13} /> Adicionar codigo
            </button>
            <button type="button" title="Excluir esta familia"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); void excluirFamilia(f.monitor_item_id, f.descricao || ''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px 6px', display: 'flex', alignItems: 'center' }}>
              <Trash2 size={15} />
            </button>
          </span>
        </summary>

        <div style={{ borderTop: '1px solid var(--panel-border)' }}>
          {/* Sem thead: o cabecalho e' UM so, fixo no topo da pagina (CabecalhoColunasMonitor).
              As larguras vem do mesmo colgroup, senao as colunas nao batem com ele. */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <ColunasMonitor />
            <tbody>
              {f.ofertas.map((o, idx) => {
                const win = o.melhor && o.disponivel;
                const arrastandoEsta = dragFam === f.monitor_item_id && dragIdx === idx;
                const alvoDrop = dragFam === f.monitor_item_id && dragOverIdx === idx && dragIdx !== idx;
                const selecionada = ofertaSel === o.oferta_id;
                // Cor do FORNECEDOR so nas duas primeiras celulas (setas + codigo) — pedido dele,
                // 07/10/2026: Martins azul/branco, Tambasa turquesa/preto. O resto da linha fica com o
                // fundo de sempre (amarelo = melhor oferta, azul-claro = linha clicada), e a melhor
                // oferta ainda leva moldura preta + selo MELHOR. Grade BRANCA entre as celulas.
                const corLinha = corDoFornecedor(o.fornecedor_nome);
                // Celula pintada (amarela ou do fornecedor): detalhe interno herda a fonte dela —
                // cinza/azul do tema somem sobre fundo solido.
                const pinta = win;
                const pintaCod = win || corLinha != null;
                const tdBase: CSSProperties = {
                  padding: '9px 12px', textAlign: 'right', border: '1px solid #fff',
                  whiteSpace: 'nowrap', verticalAlign: 'middle',
                  ...(win ? { background: 'var(--positive-bg)', color: 'var(--positive-fg)' } : {}),
                  // Indisponivel NAO esmaece o texto (pedido dele, 07/10/2026): o cinza #9ca3af dava
                  // 1,87:1 sobre o painel — ilegivel. Quem diz "indisponivel" e o selo solido da linha.
                  ...(selecionada && !win ? { background: '#bfdbfe', color: '#000' } : {}),
                };
                const tdCod: CSSProperties = corLinha ? { ...tdBase, background: corLinha.bg, color: corLinha.fg } : tdBase;
                const moldura = win && selecionada ? '6px double #000'
                  : win ? '4px solid #000'
                  : selecionada ? '3px dashed #000'
                  : null;
                return (
                  <Fragment key={o.oferta_id}>
                  <tr
                    onClick={() => setOfertaSel(selecionada ? null : o.oferta_id)}
                    aria-selected={selecionada}
                    onDragEnter={(e) => { if (dragFam === f.monitor_item_id && dragIdx !== null && dragIdx !== idx) { e.preventDefault(); setDragOverIdx(idx); } }}
                    onDragOver={(e) => { if (dragFam === f.monitor_item_id && dragIdx !== null) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); if (dragFam === f.monitor_item_id && dragIdx !== null && dragIdx !== idx) void soltarOferta(f.monitor_item_id, dragIdx, idx); limparDrag(); }}
                    style={{
                      cursor: 'pointer',
                      ...(arrastandoEsta ? { opacity: 0.45 } : {}),
                      ...(moldura ? { outline: moldura, outlineOffset: win ? (selecionada ? '-6px' : '-4px') : '-3px' } : {}),
                      ...(alvoDrop ? { outline: '2px solid var(--primary)', outlineOffset: '-2px' } : {}),
                    }}
                  >
                    <td style={{ ...tdCod, textAlign: 'center', width: 42, padding: '9px 4px 9px 8px', color: corLinha ? corLinha.fg : win ? 'inherit' : 'var(--muted, #9ca3af)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <span
                          draggable
                          onDragStart={(e) => { setDragFam(f.monitor_item_id); setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* ignora */ } }}
                          onDragEnd={limparDrag}
                          style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center' }}
                          title="Arraste para reordenar"
                        >
                          <GripVertical size={14} style={{ display: 'block' }} />
                        </span>
                        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                          <button type="button" title="Mover para cima"
                            onClick={() => void moverOferta(f.monitor_item_id, idx, -1)} disabled={idx === 0}
                            style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 1 }}>
                            <ChevronUp size={13} />
                          </button>
                          <button type="button" title="Mover para baixo"
                            onClick={() => void moverOferta(f.monitor_item_id, idx, 1)} disabled={idx === f.ofertas.length - 1}
                            style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === f.ofertas.length - 1 ? 'default' : 'pointer', opacity: idx === f.ofertas.length - 1 ? 0.25 : 1 }}>
                            <ChevronDown size={13} />
                          </button>
                        </span>
                      </span>
                    </td>
                    {/* Com preco digitado a linha volta a ter as 4 colunas de valor, entao o selo
                        amarelo perde a celula larga onde morava — e ele NAO pode sumir junto: e o
                        que avisa que o codigo do cadastro defasou e pede a busca do novo por nome
                        (DA-288). Muda de casa, pra ao lado do codigo, que e do que ele fala. Na
                        linha sem preco nenhum segue onde estava: duas casas pro mesmo aviso
                        duplicariam a informacao em 25 linhas que ninguem pediu pra mexer. */}
                    {/* whiteSpace normal: na coluna de 102px "TRAMONTINA GO", o selo e a sugestao QUEBRAM
                        linha (pedido dele, 28/10/2026) — o codigo nao tem espaco, entao nunca quebra. */}
                    <td style={{ ...tdCod, textAlign: 'center', whiteSpace: 'normal', ...(corLinha ? { color: corLinha.fg } : {}) }}>
                      <span style={num}>{o.codigo_fornecedor}</span>
                      {/* O FORNECEDOR da linha, sempre escrito (DA-304: o selo do cabecalho da familia
                          saiu porque mentia com origens misturadas — desde a Tambasa a familia mistura
                          de verdade). Texto, nao cor: e o que separa as duas no olho dele. */}
                      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, lineHeight: 1.1, marginTop: 1, color: pintaCod ? 'inherit' : 'var(--muted, #6b7280)' }}>
                        {(o.fornecedor_nome || '').toUpperCase()}
                      </div>
                      {o.usa_preco_manual && o.motivo === 'codigo_nao_encontrado' && (
                        <div
                          title={`O robo nao achou este codigo no site da ${o.fornecedor_nome} — nem pela descricao, quando o cadastro tem uma. O preco desta linha e o que voce digitou; se o codigo mudou, cadastre o novo pra o robo voltar a ler sozinho.`}
                          style={{ marginTop: 3, display: 'inline-block', fontSize: 9, border: '1px solid #f59e0b', background: '#fde68a', color: '#000', fontWeight: 600, borderRadius: 5, padding: '0 5px', ...num }}
                        >cod. nao encontrado</div>
                      )}
                      {o.usa_preco_manual && o.motivo === 'codigo_nao_encontrado' && o.sugestao_codigo && (
                        <div title={`A busca pela descricao achou "${o.sugestao_descricao ?? ''}" com outro codigo. Confira e, se for o mesmo produto, troque o codigo no lapis da linha.`}
                          style={{ marginTop: 2, fontSize: 9.5, fontWeight: 700, ...num }}>
                          sugestao: {o.sugestao_codigo}
                        </div>
                      )}
                    </td>
                    {/* Foto do CODIGO: clique abre o modal de colar (Ctrl+V); passar o mouse amplia.
                        Miniatura de 41px nao distingue rolo de bobina — o preview no hover distingue. */}
                    <td style={{ ...tdBase, textAlign: 'center', width: 55, padding: '4px 6px' }}>
                      {(() => {
                        const foto = o.foto_url; // colada no codigo > produto vinculado no catalogo do fornecedor
                        return (
                          <button
                            type="button"
                            title={foto ? 'Trocar a foto deste codigo' : 'Colar uma foto pra identificar este codigo'}
                            onClick={(e) => { e.stopPropagation(); setFotoHover(null); setFotoOferta(o); }}
                            onMouseEnter={(e) => {
                              if (!foto) return;
                              const r = e.currentTarget.getBoundingClientRect();
                              setFotoHover({ url: foto, x: r.right + 8, y: r.top });
                            }}
                            onMouseLeave={() => setFotoHover(null)}
                            style={{
                              width: 41, height: 41, padding: 0, margin: '0 auto', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 4,
                              background: 'none',
                              border: foto ? '1px solid transparent' : `1px dashed ${pinta ? 'currentColor' : 'var(--panel-border)'}`,
                              color: pinta ? 'inherit' : 'var(--muted, #9ca3af)',
                            }}
                          >
                            {foto
                              ? <img src={foto} alt="" style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: 3 }} />
                              : <ImagePlus size={16} />}
                          </button>
                        );
                      })()}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'left', color: '#000' }}>
                      {/* De-para DESTE codigo -> nosso produto (0203), ACIMA da descricao do fornecedor.
                          Ate aqui so aparecia ao abrir a edicao — vinculo que so se descobre clicando
                          nao existe pra quem olha a tela. Sem vinculo o rotulo APARECE mesmo assim:
                          ausencia calada nao se distingue de "nao tem", e e justamente o codigo sem
                          dono que precisa ser visto. O codigo interno nunca corta (e identificador);
                          quem cede espaco e a descricao. */}
                      {/* AZUL (pedido dele, 28/10/2026) separa o NOSSO produto da descricao do fornecedor
                          logo abaixo. Hex fixo e escuro, como o #000 da celula: 5,3:1 sobre o amarelo do
                          MELHOR (o --primary #2563eb dava 4,1). */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, lineHeight: 1.25, marginBottom: 2, color: '#1d4ed8' }}>
                        {o.produto_id ? (
                          <>
                            <Link2 size={11} style={{ flexShrink: 0 }} />
                            <span style={{ ...num, fontWeight: 700, flexShrink: 0 }}>
                              {o.produto_codigo || `#${o.produto_id}`}
                            </span>
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={o.produto_descricao || ''}>
                              {o.produto_descricao || '(sem descricao)'}
                            </span>
                          </>
                        ) : (
                          <span style={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                            sem produto vinculado
                          </span>
                        )}
                      </span>
                      {o.produto_id && o.produto_estoque != null && (
                        <span style={{ display: 'block', fontSize: 13, lineHeight: 1.25, marginBottom: 2, whiteSpace: 'nowrap' }}
                          title="Quantidade em estoque do produto vinculado a esta linha">
                          Estoque: <span style={{ ...num, fontWeight: 700 }}>{formatQtd(o.produto_estoque)}</span>
                        </span>
                      )}
                      <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', lineHeight: 1.22, fontSize: 13 } as CSSProperties}>
                        {/* Sem descricao nenhuma (nem digitada, nem lida, nem no historico) diz isso —
                            cair na MARCA aqui parecia descricao de verdade e repetia a coluna Marca
                            (406383, 07/10/2026: "COBRECOM" no lugar do nome, que nunca existiu). */}
                        {o.descricao_fornecedor || <span style={{ fontStyle: 'italic', opacity: 0.75 }}>sem descricao</span>}
                      </span>
                    </td>
                    {/* MARCA em coluna propria: ja esta na descricao, mas separada da visibilidade
                        pra bater marca contra marca no olho. Sem a marca no cadastro, cai na lida
                        da descricao do site (o backend resolve em marca_exibida). */}
                    <td style={{ ...tdBase, textAlign: 'left' }}>
                      {o.marca_exibida && o.marca_exibida !== '—'
                        ? o.marca_exibida.toUpperCase()
                        : <span style={{ color: pinta ? 'inherit' : 'var(--muted, #9ca3af)' }}>—</span>}
                    </td>
                    {/* QTD da embalagem: e o divisor do custo unitario e ate hoje so vivia no cadastro,
                        entao uma qtd errada (codigo reatribuido pela Martins) so aparecia como um custo
                        estranho. Aqui a caracteristica fica no olho — inclusive na linha esgotada. */}
                    <td style={{ ...tdBase, ...num }}>
                      {o.qtd > 0 ? o.qtd.toLocaleString('pt-BR') : <span style={{ color: pinta ? 'inherit' : 'var(--muted, #9ca3af)' }}>—</span>}
                    </td>
                    {/* A caixa do DIFAL mora DEBAIXO da UF, na mesma celula ([DA-727]): coluna propria
                        custava 110px e fazia a FLEX 2,5MM VERDE passar 34px da tela (medido numa
                        janela de 1446). O DIFAL e' consequencia da UF, entao a vizinhanca e' a certa.
                        Nasce vazia ("a conferir", decisao dele) e so pesa quando a oferta vem de MG;
                        enquanto vazia o custo usa 4,49%. Vazia = laranja solido + preto (pendente).
                        ⚠️ Linha de GO NAO mostra a caixa (pedido dele, 12/09): ali nao ha DIFAL. A UF e'
                        medida a cada coleta, entao se a oferta virar MG a caixa aparece com o que ja
                        estava gravado. */}
                    <td style={{ ...tdBase, textAlign: 'center', paddingLeft: 2, paddingRight: 2 }}>
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <span style={{ ...seloUf(o.uf), display: 'inline-block', ...(pinta
                        // Sobre o fundo solido da linha o tint translucido some (medido: GO/MG ilegiveis
                        // no azul da Martins) — o selo vira pastilha opaca com o mesmo matiz.
                        ? (o.uf || '').toUpperCase() === 'MG'
                          ? { background: '#f3e8ff', color: '#6b21a8', borderColor: 'rgba(0,0,0,0.35)' }
                          : { background: '#fff', color: '#1e3a8a', borderColor: 'rgba(0,0,0,0.35)' }
                        : {}) }}>{o.uf}</span>
                      {(o.uf || '').toUpperCase() === 'MG' && <span style={{ color: pinta ? 'inherit' : '#d97706', fontSize: 10, ...num }}>+DIFAL</span>}
                      </span>
                      {(o.uf || '').toUpperCase() !== 'GO' && (
                      <select
                        className="select-seta-curta"
                        onClick={(e) => e.stopPropagation()}
                        value={o.difal_pct == null ? '' : String(o.difal_pct)}
                        disabled={!!salvandoDifal[o.oferta_id]}
                        onChange={(e) => void salvarDifal(o.oferta_id, e.target.value)}
                        title={o.difal_pct == null
                          ? 'Aliquota do DIFAL ainda nao conferida — o custo de MG usa 4,49% ate voce escolher.'
                          : 'Aliquota do DIFAL deste codigo (vale quando ele vem de MG). 4,49% = nacional; 7,87% = importado.'}
                        style={{
                          fontSize: 10, padding: '2px 2px', width: '100%', cursor: 'pointer', ...num,
                          ...(o.difal_pct == null
                            ? { '--input-bg': '#f97316', '--input-fg': '#000', '--input-border': '#c2410c', fontWeight: 600 }
                            : {}),
                        } as CSSProperties}
                      >
                        {/* "%" e nao "a conferir": a coluna UF tem 62px e o texto longo cortava (pedido dele, 28/10/2026). */}
                        <option value="">%</option>
                        {ALIQUOTAS_DIFAL.map((a) => (
                          <option key={a} value={String(a)}>{a.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%</option>
                        ))}
                      </select>
                      )}
                      </span>
                    </td>
                    {o.disponivel ? (
                      <>
                        {/* O lapis diz que este preco foi DIGITADO — e o mesmo simbolo do botao que
                            o edita. Forma, nao cor: a distincao tem que sobreviver ao daltonismo. */}
                        <td style={{ ...tdBase, ...num, whiteSpace: 'normal', paddingLeft: 6, paddingRight: 6 }}>
                          {o.usa_preco_manual ? (
                            <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, justifyContent: 'flex-end', width: '100%', whiteSpace: 'nowrap' }}
                              title="Preco digitado a mao — o robo nao consegue ler este codigo hoje.">
                              <Pencil size={11} style={{ opacity: 0.75, flexShrink: 0 }} />
                              {m(o.preco_embalagem)}
                            </span>
                          ) : m(o.preco_embalagem)}
                          {/* [DA-804] Preco de VAREJO: nao e o que o representante cobra. */}
                          {o.preco_de_site && !o.usa_preco_manual && (
                            <div style={{ fontSize: 9.5, fontWeight: 600, lineHeight: 1.1, marginTop: 1 }}>a vista no site</div>
                          )}
                        </td>
                        <td style={{ ...tdBase, ...num, whiteSpace: 'normal', paddingLeft: 6, paddingRight: 6 }}>
                          {win && (
                            <span title="Menor custo unitario da familia"
                              style={{ display: 'inline-block', marginRight: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, padding: '1px 6px', borderRadius: 4, background: '#000', color: '#fde68a', verticalAlign: 'middle' }}>
                              MELHOR
                            </span>
                          )}
                          <b>{m(o.custo_unit, 4)}</b>
                          {/* [DA-804] Custo ESTIMADO, nao de proposta: diz a conta e de onde vem o fator. */}
                          {/* Sem site de referencia, o custo E o preco do representante: nao ha estimativa. */}
                          {o.custo_do_representante && o.preco_representante != null && (
                            <div title={`Preco do representante (proposta): ${formatMoedaSemSimbolo(o.preco_representante, 4)} pela embalagem de ${o.qtd}. O site nao tem este codigo, entao nao ha fator — quando o site passar a ter, a linha volta a seguir o site x fator.`}
                              style={{ fontSize: 9.5, fontWeight: 600, lineHeight: 1.1, marginTop: 1 }}>
                              representante
                            </div>
                          )}
                          {o.representante_nao_fecha && o.preco_representante != null && (
                            <div title={`O preco do representante digitado (${formatMoedaSemSimbolo(o.preco_representante, 4)}) nao fecha com o site: o fator sairia fora de 0 a 100%. Confira o preco da caixa e a Qtd no lapis. Enquanto isso, o custo usa o fator do topo.`}
                              // Linha PROPRIA sob o custo, alinhada a direita: inline-block ficava ao lado do
                              // numero e invadia a coluna COLETADO (print dele, 09/10).
                              style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.15, marginTop: 2, background: '#f97316', color: '#000', borderRadius: 4, padding: '1px 4px', display: 'table', marginLeft: 'auto', whiteSpace: 'normal', textAlign: 'right' }}>
                              representante nao fecha
                            </div>
                          )}
                          {o.preco_de_site && o.fator_aplicado_pct != null && !o.custo_do_representante && (
                            <div title={o.preco_representante != null && o.preco_site_referencia != null
                              ? `Estimado: preco do site x o fator desta linha, calculado do preco do representante ${formatMoedaSemSimbolo(o.preco_representante, 4)} contra o site ${formatMoedaSemSimbolo(o.preco_site_referencia, 2)}${o.fator_calibrado_em ? ` em ${new Date(o.fator_calibrado_em).toLocaleDateString('pt-BR')}` : ''}.`
                              : o.fator_site_pct != null
                              ? 'Estimado: preco do site x o fator PROPRIO desta linha (lapis).'
                              : 'Estimado: preco do site x o fator da Tramontina (caixa no topo).'}
                              style={{ fontSize: 9.5, fontWeight: 600, lineHeight: 1.1, marginTop: 1 }}>
                              estimado · site × {o.fator_aplicado_pct.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}%{o.fator_site_pct != null ? ' (linha)' : ''}
                            </div>
                          )}
                        </td>
                        {/* Numero digitado NAO pode passar por leitura do robo: aqui a data cede o
                            lugar a palavra que diz de onde o preco veio (ver DA-289 / DA-603). */}
                        <td style={{ ...tdBase, ...num, fontSize: 10.5, color: pinta ? 'inherit' : 'var(--panel-fg, #111)' }}>
                          {o.usa_preco_manual ? (
                            <span style={{ fontStyle: 'italic' }} title="Valor digitado no cadastro desta oferta, nao coletado no site.">
                              digitado {o.preco_manual_em
                                ? new Date(o.preco_manual_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                                : '—'}
                            </span>
                          ) : o.coletado_em
                            ? new Date(o.coletado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : '—'}
                        </td>
                        <td style={{ ...tdBase, ...num, fontWeight: 700 }}>{m(o.fc_pix_unit, 4)}</td>
                      </>
                    ) : (
                      <td colSpan={4} style={{ ...tdBase, textAlign: 'center' }}>
                        {o.motivo === 'codigo_nao_encontrado' ? (
                          <span
                            title={`O robo nao achou este codigo no site da ${o.fornecedor_nome} — nem pela descricao, quando o cadastro tem uma (linha sem descricao so e pesquisada pelo codigo). Se o codigo mudou, procure o novo e cadastre.`}
                            style={{ fontSize: 10, border: '1px solid #f59e0b', background: '#fde68a', color: '#000', fontWeight: 600, borderRadius: 5, padding: '1px 7px', ...num }}
                          >codigo nao encontrado</span>
                        ) : null}
                        {o.motivo === 'codigo_nao_encontrado' && o.sugestao_codigo ? (
                          // A busca pela DESCRICAO achou o mesmo nome com OUTRO codigo ([DA-797]).
                          // Sugestao, nunca troca: a saboneteira da DA-288 entrou por troca automatica.
                          <div title="Confira no site e, se for o mesmo produto, troque o codigo no lapis da linha."
                            style={{ marginTop: 4, fontSize: 11, lineHeight: 1.3 }}>
                            <span style={{ fontWeight: 700 }}>Sugestao: </span>
                            <span style={{ ...num, fontWeight: 700 }}>{o.sugestao_codigo}</span>
                            {o.sugestao_descricao ? <span> — {o.sugestao_descricao}</span> : null}
                          </div>
                        ) : null}
                        {o.motivo === 'codigo_nao_encontrado' ? null : !o.coletado_em ? (
                          // NUNCA lida (zero linhas em monitor_coleta): nao e "indisponivel" — o robo nem
                          // olhou. Afirmar indisponivel aqui mentia (017915 da Tambasa, 07/10/2026: R$ 229,62
                          // no site, cadastrado e nunca pedido). Pendencia de 23/09.
                          <span title="Este codigo ainda nao foi lido pelo robo. Clique no botao de atualizar da linha (ou Atualizar da familia) com a janela do robo aberta."
                            style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 5, padding: '2px 8px', ...num,
                              background: '#fff', color: '#000', border: '1px dashed #6b7280' }}>
                            {o.coleta_solicitada_em ? 'aguardando o robo ler' : 'ainda nao lido — clique em atualizar'}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 5, padding: '2px 8px', ...num,
                            background: '#e5e7eb', color: '#000', border: '1px solid #6b7280' }}>indisponivel no site</span>
                        )}
                      </td>
                    )}
                    <td style={{ ...tdBase, textAlign: 'right', paddingLeft: 4, paddingRight: 8 }}>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        {/* Salvar no Catalogo do Fornecedor (DA-370). Ja salvo = botao CINZA e desabilitado
                            (nao da pra salvar 2x); alem da cor, a FORMA distingue (daltonismo): + = salvar,
                            check = ja no catalogo. Cinza forcado mesmo na linha amarela (win). */}
                        {o.no_catalogo ? (
                          <button type="button" disabled
                            title="Ja no Catalogo do Fornecedor (vinculado ao Monitor)"
                            style={{ background: 'none', border: 'none', color: pinta ? 'inherit' : 'var(--muted, #9ca3af)', padding: 2, cursor: 'not-allowed', opacity: 0.6 }}>
                            <BookmarkCheck size={14} />
                          </button>
                        ) : (
                          // [DA-804] p.9 — na linha de varejo (Tramontina) vai o CUSTO ESTIMADO x qtd da caixa.
                          <button type="button"
                            title={o.preco_de_site
                              ? 'Salvar no Catalogo do Fornecedor o CUSTO ESTIMADO (custo unit. x qtd da caixa) — ele acompanha cada leitura do site'
                              : 'Salvar no Catalogo do Fornecedor (e vincular ao Monitor)'}
                            onClick={() => salvarNoCatalogo(o.oferta_id)}
                            disabled={!!salvandoCatalogo[o.oferta_id]}
                            style={{ background: 'none', border: 'none', cursor: salvandoCatalogo[o.oferta_id] ? 'default' : 'pointer', color: pinta ? 'inherit' : 'var(--primary)', padding: 2, opacity: salvandoCatalogo[o.oferta_id] ? 0.5 : 1 }}>
                            <BookmarkPlus size={14} className={salvandoCatalogo[o.oferta_id] ? 'animate-pulse' : ''} />
                          </button>
                        )}
                        {o.coleta_solicitada_em && !solicitandoOferta[o.oferta_id] ? (
                          <button type="button" title="Cancelar a coleta pendente deste codigo"
                            onClick={() => cancelarColetaOferta(o.oferta_id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: pinta ? 'inherit' : '#b45309', padding: 2 }}>
                            <X size={14} />
                          </button>
                        ) : (
                          <button type="button" title="Atualizar o preco so deste codigo"
                            onClick={() => solicitarColetaOferta(o.oferta_id)}
                            disabled={!!solicitandoOferta[o.oferta_id] || !!o.coleta_solicitada_em}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: pinta ? 'inherit' : 'var(--primary)', padding: 2 }}>
                            <RefreshCw size={14} className={(solicitandoOferta[o.oferta_id] || o.coleta_solicitada_em) ? 'animate-spin' : ''} />
                          </button>
                        )}
                        <button type="button" title="Editar oferta" onClick={() => { setEditandoOferta(o.oferta_id); setAddCodPara(null); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: pinta ? 'inherit' : 'var(--muted, #6b7280)', padding: 2 }}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" title="Mover para outra familia" onClick={() => setTrocarFamilia({ oferta: o, familiaId: f.monitor_item_id })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: pinta ? 'inherit' : 'var(--primary)', padding: 2 }}>
                          <ArrowRightLeft size={14} />
                        </button>
                        <button type="button" title="Remover oferta" onClick={() => removerOferta(o.oferta_id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: pinta ? 'inherit' : '#dc2626', padding: 2 }}>
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </td>
                  </tr>
                  {editandoOferta === o.oferta_id && (
                    <tr>
                      <td colSpan={12} style={{ borderTop: '1px solid var(--panel-border)', background: 'var(--page-bg)', padding: 0 }}>
                        <OfertaForm
                          fornecedores={fornecedores}
                          inicial={o}
                          onCancelar={() => setEditandoOferta(null)}
                          onSalvar={async (payload) => {
                            try {
                              const r = await api.put<{ catalogo_vinculo?: string | null }>(`/api/monitor-precos/ofertas/${o.oferta_id}`, payload);
                              setEditandoOferta(null);
                              await carregar();
                              avisarVinculoCatalogo(r?.catalogo_vinculo, 'Oferta atualizada');
                            } catch (e) { toast.erro((e as Error).message); }
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
              {f.ofertas.length === 0 && (
                <tr><td colSpan={12} style={{ padding: '14px 12px', textAlign: 'center', fontSize: 13, color: '#000' }}>Sem ofertas cadastradas.</td></tr>
              )}
              {addCodPara === f.monitor_item_id && (
                <tr id={`monitor-addcod-${f.monitor_item_id}`}>
                  <td colSpan={12} style={{ borderTop: '1px solid var(--panel-border)', background: 'var(--page-bg)', padding: 0 }}>
                    <OfertaForm
                      fornecedores={fornecedores}
                      fornecedorPadraoId={fornecedorPadraoFam}
                      codigoInicial={codigoPre[f.monitor_item_id]}
                      produtoPadrao={f.produto}
                      onCancelar={() => { setAddCodPara(null); setCodigoPre((s) => { const c = { ...s }; delete c[f.monitor_item_id]; return c; }); }}
                      onSalvar={async (payload) => {
                        try {
                          const r = await api.post<{ catalogo_vinculo?: string | null }>('/api/monitor-precos/ofertas', { ...payload, monitor_item_id: f.monitor_item_id });
                          setAddCodPara(null);
                          setCodigoPre((s) => { const c = { ...s }; delete c[f.monitor_item_id]; return c; });
                          await carregar();
                          avisarVinculoCatalogo(r?.catalogo_vinculo, 'Codigo adicionado');
                        } catch (e) { toast.erro((e as Error).message); }
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
            {/* MEDIA das opcoes desta familia, debaixo das colunas que ela resume (Custo unit. e
                Sugestao de Venda). So entra oferta com preco: linha ND nao tem custo, e incluir
                zero rebaixaria a media. O rotulo diz sobre QUANTAS a conta foi feita, senao ele
                afirma "media da familia" quando parte das opcoes ficou de fora. */}
            {(() => {
              const comCusto = f.ofertas.filter((o) => o.disponivel && o.custo_unit != null);
              // UMA linha de media POR FORNECEDOR (pedido dele, 07/10/2026: uma oferta da Tambasa
              // distorcia a media das quatro da Martins). Ordem = a da primeira oferta de cada um na
              // lista. Cada linha tem o PROPRIO botao, que grava a media DAQUELE fornecedor — quem
              // manda no preco e' escolhido no clique, nada vem pre-escolhido.
              const grupos: { fornecedorId: number | null; fornecedorNome: string | null; ofertas: OfertaApi[] }[] = [];
              for (const o of comCusto) {
                let g = grupos.find((x) => x.fornecedorId === o.fornecedor_id);
                if (!g) { g = { fornecedorId: o.fornecedor_id, fornecedorNome: o.fornecedor_nome, ofertas: [] }; grupos.push(g); }
                g.ofertas.push(o);
              }
              // ⛔ A linha NAO some quando nao ha custo: ela e' a unica porta pra regua desta familia,
              // e sumir justo no estado que produz a duvida deixa o operador sem nada pra clicar.
              // 3 familias vivem assim hoje (9 codigos cadastrados que o coletor nunca visitou):
              // FLEX 2,5MM AMARELO/BRANCO e FLEX 4MM VERMELHO. Sem media, a linha diz o motivo.
              if (grupos.length === 0) grupos.push({ fornecedorId: null, fornecedorNome: null, ofertas: [] });
              const tdMedia: CSSProperties = {
                padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap', background: 'var(--page-bg)',
              };
              return (
                <tfoot>
                  {grupos.map((g, gi) => {
                    const temCusto = g.ofertas.length > 0;
                    const mediaCusto = temCusto
                      ? g.ofertas.reduce((s, o) => s + (o.custo_unit as number), 0) / g.ofertas.length
                      : null;
                    const comVenda = g.ofertas.filter((o) => o.fc_pix_unit != null);
                    const mediaVenda = comVenda.length
                      ? comVenda.reduce((s, o) => s + (o.fc_pix_unit as number), 0) / comVenda.length
                      : null;
                    // Margem entre os DOIS numeros que esta linha ja mostra (pedido do Ruan, 29/08).
                    // A formula e' a DELE, da planilha "Composicao de Preco" que ele usa pra fechar
                    // preco: (venda ÷ custo) − 1 — a mesma coluna DIF de la (1,71 / 1,31 = 30,00%).
                    // ⚠️ NAO e' conferencia independente: a Sugestao de Venda e' CONSTRUIDA como
                    // custo × markup, entao o numero DEVOLVE o markup da familia (30% -> 30,00%).
                    // Ele varia entre familias porque o markup varia, nunca dentro de uma.
                    // Calculada sobre os valores EXIBIDOS, e nao sobre os conjuntos internos: e' o que
                    // deixa ele conferir a conta na propria tela, sem depender de mim.
                    const margem = mediaCusto != null && mediaVenda != null && mediaCusto > 0
                      ? mediaVenda / mediaCusto - 1
                      : null;
                    const td: CSSProperties = {
                      ...tdMedia,
                      borderTop: gi === 0 ? '2px solid var(--panel-border)' : '1px solid var(--panel-border)',
                    };
                    return (
                      <tr key={g.fornecedorId ?? 'sem'}>
                        {/* Rotulo em --panel-fg, nao no cinza de --muted: o token nao existe no Azul
                            Royal e o fallback #6b7280 sobre o --page-bg mede contraste 2,28 (minimo 4,5).
                            Com --panel-fg da 7,88 no mesmo tema. */}
                        <td colSpan={8} style={{ ...td, color: 'var(--panel-fg, #1f2937)', fontSize: 11 }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
                            <span>
                              {temCusto
                                ? <>Media <b>{g.fornecedorNome}</b> de {contar(g.ofertas.length, 'opcao', 'opcoes')} com preco</>
                                : 'Sem preco coletado — nao ha media pra aplicar'}
                            </span>
                            {/* A media so vira trabalho poupado se der pra APLICAR dela (DA-642). Sem regua
                                o botao continua aparecendo, dizendo o que falta — recurso que so aparece
                                depois de configurado nao e' descoberto por ninguem. */}
                            <button
                              type="button"
                              className="btn-primario"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPrecosPara({ familia: f, fornecedorId: g.fornecedorId, fornecedorNome: g.fornecedorNome }); }}
                              style={{ fontSize: 11.5, padding: '4px 10px', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              {f.regua?.length
                                ? `Atualizar ${contar(f.regua.length, 'produto')}`
                                : 'Vincular produtos'}
                            </button>
                          </span>
                        </td>
                        <td style={{ ...td, ...num, fontWeight: 700, color: 'var(--panel-fg, #111)' }}>{m(mediaCusto, 4)}</td>
                        <td style={td} />
                        <td style={{ ...td, ...num, fontWeight: 700, color: 'var(--panel-fg, #111)' }}>{m(mediaVenda, 4)}</td>
                        <td style={{ ...td, ...num, fontSize: 11.5, color: 'var(--panel-fg, #1f2937)' }}>
                          {margem == null ? null : (
                            <span title="Margem entre o custo medio e a sugestao de venda media — (venda ÷ custo) − 1">
                              margem {(margem * 100).toFixed(2).replace('.', ',')}%
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tfoot>
              );
            })()}
          </table>
          <div style={{ color: 'var(--muted, #6b7280)', fontSize: 11, padding: '7px 14px', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span>Custo unit. = preco unitario (embalagem ÷ qtd){temMartins ? ' − cupom (so Martins)' : ''}{temMg ? ' + DIFAL (MG, aliquota da caixa abaixo da UF; caixa laranja "%" = a conferir, usa 4,49%)' : ''}{temSite ? ' · Tramontina: preco a vista do SITE × o fator (caixa no topo, ou o da linha) — custo ESTIMADO, nao de proposta' : ''}.</span>
            <span>Sugestao de Venda = Custo unit. × markup {Math.round(f.markup * 100)}%.</span>
          </div>
        </div>
      </details>
    );
  };

  return (
    <div style={{ width: '100%', padding: '18px 22px 50px' }}>
      <CabecalhoColunasMonitor />
      {/* Cabecalho: titulo + cupom + atualizar */}
      <div className="pagina-header" style={{ flexWrap: 'wrap' }}>
        <div>
          <h2 className="pagina-titulo">Monitor de Custos — Fornecedores</h2>
          <div style={{ color: 'var(--muted, #6b7280)', fontSize: 12, marginTop: 2 }}>Cotacoes › Comparador de custo · Martins · Tambasa · Tramontina</div>
        </div>
        <div className="pagina-acoes" style={{ flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--input-bg)', border: '1px solid var(--panel-border)', borderRadius: 10, padding: '7px 11px' }}>
            <label style={{ color: 'var(--muted, #6b7280)', fontSize: 12 }}>Cupom Martins da semana</label>
            <input
              value={cupomInput}
              onChange={(e) => setCupomInput(e.target.value)}
              onBlur={salvarCupom}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              style={{ width: 52, ...num, textAlign: 'right' } as CSSProperties}
              inputMode="decimal"
            />
            <span style={{ color: 'var(--muted, #6b7280)' }}>%</span>
          </div>
          {/* [DA-804] O robo da Tramontina le o a vista do SITE; o custo do representante e esta
              fracao dele. Vale pra toda linha da Tramontina que nao tem fator proprio (lapis). */}
          <div title="O robo da Tramontina le o preco a vista do site. O custo do representante e este percentual dele (46,28% medido na proposta de 08/10). Linha com fator proprio (no lapis) usa o dela."
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--input-bg)', border: '1px solid var(--panel-border)', borderRadius: 10, padding: '7px 11px' }}>
            <label style={{ color: 'var(--muted, #6b7280)', fontSize: 12 }}>Tramontina = site ×</label>
            <input
              value={fatorInput}
              onChange={(e) => setFatorInput(e.target.value)}
              onBlur={salvarFator}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              style={{ width: 60, ...num, textAlign: 'right' } as CSSProperties}
              inputMode="decimal"
            />
            <span style={{ color: 'var(--muted, #6b7280)' }}>%</span>
          </div>
          {coletores != null && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {coletores.map(({ fornecedor, online }) => (
                <div
                  key={fornecedor}
                  title={fornecedor === 'Tramontina'
                    // [DA-804] o robo da Tramontina mora no SERVIDOR — nao ha janela pra abrir.
                    ? (online
                      ? 'O robo da Tramontina roda no servidor e esta respondendo — nao precisa abrir janela nenhuma.'
                      : 'O robo da Tramontina roda no servidor e nao esta respondendo. Ele volta sozinho em segundos depois de uma publicacao; se continuar assim, avise.')
                    : online
                    ? `A janela "Atualizar Precos ${fornecedor}" esta aberta e respondendo — da pra coletar as linhas da ${fornecedor}.`
                    : `A janela "Atualizar Precos ${fornecedor}" NAO esta aberta no seu computador. Abra o atalho na Area de Trabalho pra coletar as linhas da ${fornecedor}.`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600,
                    padding: '2px 10px', borderRadius: 999, whiteSpace: 'nowrap',
                    background: online ? 'rgba(37,99,235,0.12)' : '#f97316',
                    color: online ? 'var(--primary, #2563eb)' : '#000',
                    border: online ? '1px solid rgba(37,99,235,0.35)' : '1px solid #c2410c',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: online ? 'var(--primary, #2563eb)' : '#000' }} />
                  {fornecedor} {online ? 'online' : 'offline'}
                </div>
              ))}
            </div>
          )}
          <button type="button" className="btn-primario" onClick={atualizar} disabled={atualizando} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <RefreshCw size={15} className={atualizando ? 'animate-spin' : ''} /> Atualizar precos
          </button>
          {coletaFmt && (
            <div style={{ color: 'var(--muted, #6b7280)', fontSize: 11, textAlign: 'right', lineHeight: 1.4 }}>
              Coletado<br />{coletaFmt}
            </div>
          )}
        </div>
      </div>

      {/* COLETA RECUSADA: o sistema barrou um lote que apagaria precos (Martins deslogada, ou a
          maioria dos codigos voltando "indisponivel" de uma vez). Sem esta faixa a tela apenas
          fica IGUAL depois do clique — e "nada mudou" se le como "o botao nao funciona", que foi
          o relato de 02/09/2026. Fica ate a proxima coleta que grave de verdade; nao e' toast.
          Laranja solido + preto: aviso que nao depende de distinguir verde de vermelho. */}
      {dados.aviso_coleta && (
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '11px 14px', background: '#f97316', color: '#000',
            borderRadius: 11, marginBottom: 12, fontSize: 13, lineHeight: 1.45,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1.2 }} aria-hidden>⚠</span>
          <div>
            <strong>A ultima coleta foi recusada — nenhum preco mudou.</strong>
            <div style={{ marginTop: 2 }}>{dados.aviso_coleta.texto}</div>
          </div>
        </div>
      )}

      {/* Buscar produto na Martins por NOME (descoberta de codigo) */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '11px 14px', background: 'var(--panel-bg)', border: '1px solid var(--panel-border)',
          borderRadius: 11, marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Buscar produto na Martins por nome</span>
          <span style={{ color: 'var(--muted, #6b7280)', fontSize: 11 }}>acha o codigo novo quando a Martins troca + lista similares (vitrine)</span>
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted, #9ca3af)' }} />
          <UpperInput
            value={buscaTermo}
            onChange={setBuscaTermo}
            placeholder="ex: CABO FLEXIVEL 1,5MM"
            onKeyDown={(e) => { if (e.key === 'Enter') void dispararBusca(null); }}
            style={{ width: '100%', paddingLeft: 32, borderRadius: 9 } as CSSProperties}
          />
        </div>
        <button type="button" className="btn-primario" onClick={() => void dispararBusca(null)} disabled={buscando || buscaTermo.trim().length < 2}
          style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Search size={15} /> Buscar
        </button>
        {busca && (busca.status !== 'pendente') && (busca.resultado?.length ?? 0) > 0 && (
          <button type="button" className="btn-secundario" onClick={() => setBuscaModal(true)} style={{ fontSize: 12 }}>
            Ver ultimo resultado ({busca.total_resultados})
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, color: 'var(--muted, #6b7280)', fontSize: 12 }}>
        <span>{dados.total_familias} familias · {dados.total_nd} codigos indisponiveis hoje</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginLeft: 8 }}>
          <span style={{ fontWeight: 600 }}>Exibir</span>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={exibirFamilias} onChange={(e) => setExibirFamilias(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
            Familia
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={exibirGrupos} onChange={(e) => setExibirGrupos(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
            Grupo
          </label>
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-link" style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => document.querySelectorAll('details.fam-monitor').forEach((d) => ((d as HTMLDetailsElement).open = true))}>expandir todas</button>
        <span>·</span>
        <button type="button" className="btn-link" style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => document.querySelectorAll('details.fam-monitor').forEach((d) => ((d as HTMLDetailsElement).open = false))}>recolher todas</button>
      </div>

      {/* Banner prominente enquanto ha coleta por familia rodando (estilo ProgressoBusca) */}
      {temQualquerPendente && !bannerFechado && (
        <BannerColeta
          pendentes={[
            // pedido de FAMILIA (Atualizar do cabecalho) -> conta as linhas que ainda esperam robo (as de
            // um fornecedor com a janela fechada nem entram na fila, e as ja entregues saem).
            ...dados.familias
              .filter((f) => f.coleta_solicitada_em)
              .map((f) => ({ nome: f.descricao || `Familia ${f.monitor_item_id}`, solicitadaEm: f.coleta_solicitada_em as string, nOfertas: f.ofertas.filter((o) => o.coleta_solicitada_em).length || f.ofertas.length })),
            // pedido por LINHA (Atualizar da oferta) -> 1 codigo; ignora se a familia inteira ja esta pedida.
            ...dados.familias.flatMap((f) => f.ofertas
              .filter((o) => o.coleta_solicitada_em && !f.coleta_solicitada_em)
              .map((o) => ({ nome: `${f.descricao || 'Familia'} · ${o.codigo_fornecedor}`, solicitadaEm: o.coleta_solicitada_em as string, nOfertas: 1 }))),
          ]}
          total={loteRef.current.total}
          progresso={dados.coletor_progresso}
          agora={agora}
          inicioMs={loteRef.current.inicioMs}
          onFechar={() => setBannerFechado(true)}
          onCancelar={() => void cancelarTodas()}
        />
      )}

      {/* Blocos standalone (fora de contêiner) = "Familia" -> filtro "Familia". O que e "Grupo" e o
          contêiner que junta varias familias (logo abaixo). Antes a regra era o SKU pai do bloco —
          nao ha mais SKU nenhum (migration 0130), entao o que classifica e estar solto ou num grupo. */}
      {(() => {
        const standalone = dados.familias.filter((f) => f.grupo_id == null);
        const visiveis = focoId == null ? standalone
          : focoId.tipo === 'familia' ? standalone.filter(f => f.monitor_item_id === focoId.id) : [];
        if (!exibirFamilias) return null;
        return visiveis.map((f, idx) => renderFamCard(
          f, idx, visiveis.length,
          () => void moverBlocoStandalone(visiveis, idx, -1),
          () => void moverBlocoStandalone(visiveis, idx, 1),
        ));
      })()}

      {/* Grupos-contêiner (caixa que junta varios blocos) — tambem sao "Grupo" (sem SKU pai),
          entao seguem o mesmo filtro "Exibir Grupo". */}
      {exibirGrupos && (focoId == null ? dados.grupos : focoId.tipo === 'grupo' ? dados.grupos.filter(g => g.grupo_id === focoId.id) : []).map((g, gIdx) => {
        const membros = dados.familias.filter((f) => f.grupo_id === g.grupo_id);
        return (
          <details key={`grupo-${g.grupo_id}`} open className="grupo-monitor"
            style={{ marginBottom: 14, border: '2px solid var(--panel-border)', borderRadius: 12, overflow: 'hidden', background: 'var(--page-bg)' }}>
            {/* Mesmo gesto da familia (pedido dele, 09/10/2026): o clique solto no cabecalho nao
                recolhe — quem recolhe e o botao -/+ antes do nome. */}
            <summary onClick={(e) => e.preventDefault()}
              style={{ listStyle: 'none', cursor: 'default', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--accent-soft, rgba(0,0,0,0.04))' }}>
              <span style={{ display: 'inline-flex', flexDirection: 'column' }} onClick={(e) => e.preventDefault()}>
                <button type="button" title="Mover grupo para cima"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); void moverGrupo(gIdx, -1); }}
                  disabled={gIdx === 0}
                  style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'var(--primary)', cursor: gIdx === 0 ? 'default' : 'pointer', opacity: gIdx === 0 ? 0.25 : 1 }}>
                  <ChevronUp size={18} />
                </button>
                <button type="button" title="Mover grupo para baixo"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); void moverGrupo(gIdx, 1); }}
                  disabled={gIdx === dados.grupos.length - 1}
                  style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'var(--primary)', cursor: gIdx === dados.grupos.length - 1 ? 'default' : 'pointer', opacity: gIdx === dados.grupos.length - 1 ? 0.25 : 1 }}>
                  <ChevronDown size={18} />
                </button>
              </span>
              <button type="button" className="fam-sinal bg-blue-600 text-white hover:bg-blue-700"
                title="Recolher / expandir este grupo"
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  const d = (e.currentTarget as HTMLElement).closest('details') as HTMLDetailsElement | null;
                  if (d) d.open = !d.open;
                }}
                style={{ border: 'none', borderRadius: 5, width: 22, height: 22, flexShrink: 0, cursor: 'pointer', fontWeight: 700, fontSize: 16, lineHeight: '22px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} />
              {editandoGrupo === g.grupo_id ? (
                <input
                  value={nomeGrupoEdit}
                  onChange={(e) => setNomeGrupoEdit(e.target.value)}
                  onBlur={() => void salvarNomeGrupo(g.grupo_id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void salvarNomeGrupo(g.grupo_id); if (e.key === 'Escape') setEditandoGrupo(null); }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{ fontWeight: 700, fontSize: 14, background: 'var(--input-bg)', border: '1px solid var(--panel-border)', borderRadius: 6, padding: '2px 8px', minWidth: 180 }}
                />
              ) : (
                <>
                  <span style={{ fontWeight: 700, fontSize: 14, cursor: 'text' }}
                    onDoubleClick={(e) => { e.preventDefault(); setEditandoGrupo(g.grupo_id); setNomeGrupoEdit(g.nome); }}
                    title="Clique duplo para renomear">
                    {g.nome}
                  </span>
                  <button type="button" title="Renomear o grupo"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditandoGrupo(g.grupo_id); setNomeGrupoEdit(g.nome); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #9ca3af)', padding: '0 4px', lineHeight: 0, display: 'inline-flex' }}>
                    <Pencil size={13} />
                  </button>
                </>
              )}
              <span style={{ color: 'var(--muted, #6b7280)', fontSize: 12, marginLeft: 4 }}>
                {membros.length} {membros.length === 1 ? 'familia' : 'familias'}
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" className="btn-secundario"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddProdAoGrupo(g.grupo_id); setModalFamilia(true); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, padding: '4px 10px' }}>
                <Plus size={13} /> Adicionar familia
              </button>
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', color: 'var(--muted, #6b7280)', whiteSpace: 'nowrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={focoId?.tipo === 'grupo' && focoId.id === g.grupo_id}
                  style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                  onChange={(e) => { e.stopPropagation(); setFocoId(e.target.checked ? { tipo: 'grupo' as const, id: g.grupo_id } : null); }}
                />
                Ocultar os demais
              </label>
              <button type="button" title="Excluir grupo"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); void excluirGrupo(g.grupo_id, g.nome); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px 6px', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={15} />
              </button>
            </summary>
            {/* Sem respiro lateral: os blocos do grupo tem que ter a MESMA largura dos soltos, senao
                as colunas deles saem deslocadas do cabecalho unico do topo. */}
            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column' }}>
              {membros.length === 0 && (
                <div style={{ color: 'var(--muted, #6b7280)', textAlign: 'center', padding: '18px 0', fontSize: 12 }}>
                  Nenhuma familia neste grupo.
                </div>
              )}
              {membros.map((f, famIdx) => renderFamCard(
                f, famIdx, membros.length,
                () => void moverFamiliaNoGrupo(g.grupo_id, famIdx, -1),
                () => void moverFamiliaNoGrupo(g.grupo_id, famIdx, 1),
              ))}
            </div>
          </details>
        );
      })}

      {/* Botoes de adicionar */}
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button type="button" className="btn-primario" onClick={() => { setAddProdAoGrupo(null); setModalFamilia(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '5px 11px' }}>
          <Plus size={14} /> Adicionar familia
        </button>
        <button type="button" className="btn-secundario" onClick={() => setModalGrupo(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '5px 11px' }}>
          <Plus size={14} /> Novo grupo
        </button>
      </div>
      {/* Legenda */}
      <div style={{ color: 'var(--muted, #6b7280)', fontSize: 12, marginTop: 14, lineHeight: 1.7 }}>
        <b style={{ color: 'var(--text-color, inherit)' }}>Custo unit.</b> = o que o item sai por unidade naquela origem (preco da embalagem ÷ qtd − cupom, que so vale na Martins; em MG entra o DIFAL da caixa abaixo da UF, e enquanto ela esta laranja com "%" — a conferir — a conta usa 4,49%). Na Tramontina o robo le o preco a vista do SITE (varejo) e o custo e ESTIMADO: site × o fator da caixa no topo, ou o fator proprio da linha.{' '}
        <b style={{ color: 'var(--text-color, inherit)' }}>Sugestao de Venda</b> = custo × markup da familia (o preco que daria comprando ali).
        Cor da coluna Codigo = fornecedor: <span style={{ background: '#2563eb', color: '#fff', padding: '1px 6px', borderRadius: 4 }}>Martins</span>{' '}
        <span style={{ background: '#00ffff', color: '#000', padding: '1px 6px', borderRadius: 4 }}>Tambasa</span>{' '}
        <span style={{ background: '#fff', color: '#0000b7', fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>Tramontina</span>.
        A origem mais barata da familia e a <span style={{ background: 'var(--positive-bg)', color: 'var(--positive-fg)', padding: '1px 6px', borderRadius: 4 }}>linha amarela</span>, com moldura preta e o selo{' '}
        <span style={{ background: '#000', color: '#fde68a', padding: '1px 6px', borderRadius: 4, fontWeight: 700, fontSize: 10 }}>MELHOR</span>.
      </div>

      {modalFamilia && (
        <ModalAdicionarFamilia
          grupoId={addProdAoGrupo}
          onFechar={() => { setModalFamilia(false); setAddProdAoGrupo(null); }}
          onCriado={async () => { setModalFamilia(false); setAddProdAoGrupo(null); await carregar(); toast.sucesso('Familia adicionada'); }}
        />
      )}
      {trocarFamilia && (
        <ModalMoverOferta
          oferta={trocarFamilia.oferta}
          familiaAtualId={trocarFamilia.familiaId}
          familias={dados.familias}
          grupos={dados.grupos}
          onFechar={() => setTrocarFamilia(null)}
          onMovido={async (nomeDestino) => { setTrocarFamilia(null); await carregar(); toast.sucesso(`Codigo movido para ${nomeDestino}`); }}
        />
      )}
      {modalGrupo && (
        <ModalAdicionarGrupo
          onFechar={() => setModalGrupo(false)}
          onCriado={async () => { setModalGrupo(false); await carregar(); toast.sucesso('Grupo criado'); }}
        />
      )}

      {buscaModal && (
        <ModalResultadosBusca
          busca={busca}
          familias={dados.familias}
          alvoFamilia={buscaAlvoFamilia}
          iniciadaEm={buscaIniciadaEm.current}
          fornecedores={fornecedores}
          onFechar={() => setBuscaModal(false)}
          onAdicionado={() => { void carregar(); }}
        />
      )}

      {fotoOferta && (
        <ModalFotoOferta
          ofertaId={fotoOferta.oferta_id}
          codigo={fotoOferta.codigo_fornecedor}
          descricao={fotoOferta.descricao_fornecedor}
          fotoAtual={fotoOferta.foto_url}
          fotoPropria={fotoOferta.foto_propria}
          promovidaUrl={fotoOferta.foto_promovida_url}
          onFechar={(mudou) => { setFotoOferta(null); if (mudou) void carregar(); }}
        />
      )}

      {precosPara && (
        <ModalAtualizarPrecos
          aberto
          monitorItemId={precosPara.familia.monitor_item_id}
          nome={precosPara.familia.descricao}
          fornecedorId={precosPara.fornecedorId}
          fornecedorNome={precosPara.fornecedorNome}
          produtoPrincipal={precosPara.familia.produto}
          onFechar={() => setPrecosPara(null)}
          onAplicado={() => { void carregar(); }}
        />
      )}

      {/* Preview ampliado da foto no hover — `fixed` porque a tabela rola dentro do painel
          (absolute seria cortado). Vira pra esquerda quando nao cabe na direita da tela. */}
      {fotoHover && (
        <div
          style={{
            position: 'fixed', zIndex: 60, pointerEvents: 'none',
            left: fotoHover.x + 250 > window.innerWidth ? fotoHover.x - 260 : fotoHover.x,
            top: Math.min(fotoHover.y, window.innerHeight - 250),
            padding: 6, borderRadius: 8, background: 'var(--panel-bg)',
            border: '1px solid var(--panel-border)', boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
          }}
        >
          <img src={fotoHover.url} alt="" style={{ width: 230, height: 230, objectFit: 'contain', display: 'block' }} />
        </div>
      )}

      {/* Resultados do "Vincular produto". Desenhado FORA do card e ancorado no input por
          getBoundingClientRect: o `.fam-monitor` e `overflow: hidden` e engoliria um dropdown
          absoluto. Um so na tela — vincFam diz de qual familia ele e. */}
      {vincPos && vincResultados.length > 0 && (
        <div
          ref={tecladoVinculo.listaRef}
          style={{
            position: 'fixed', zIndex: 70, ...vincPos, overflowY: 'auto', borderRadius: 8, background: 'var(--panel-bg)',
            border: '1px solid var(--panel-border)', boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
          }}
        >
          {vincResultados.map((p, idx) => (
            <div
              key={p.id}
              data-idx={idx}
              onMouseEnter={() => tecladoVinculo.setHighlight(idx)}
              onClick={() => escolherVinculo(p)}
              style={{
                padding: '6px 10px', cursor: 'pointer', fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 8,
                background: idx === tecladoVinculo.highlight ? 'var(--accent-soft)' : 'transparent',
                borderTop: idx === 0 ? 'none' : '1px solid var(--page-bg)',
              }}
            >
              <MiniFotoBusca url={p.foto_url} />
              <span style={{ ...num, color: 'var(--primary)', fontWeight: 700, flexShrink: 0 }}>{p.codigo_interno}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{produtoInativo(p) && <SeloInativo />}{p.descricao}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Banner PROMINENTE (HUD unico) enquanto ha familia "aguardando coletor". Flutua centralizado por
// cima da tela. PROGRESSO REAL: o coletor reporta "codigo X de Y" a cada codigo (coletor_progresso)
// — a barra anda de verdade. Esse report tambem e o BATIMENTO: so alarma "SEM RESPOSTA" quando o
// coletor para de reportar ha mais de ~40s (janela fechada/travada); enquanto ele reporta, NUNCA
// alarma (mesmo num lote longo de minutos). Inicio ancora no pedido mais antigo (sobrevive a F5).
// Calibrado com coleta real (2026-06-24): a Martins leva ~50-55s por codigo (abre a pagina, expande
// "Mais vendedores", le os 2 armazens), e os ND/indisponiveis sao os mais lentos (esperam o timeout).
const COLETA_POR_CODIGO_MS = 50000;  // estimativa media por codigo (pro "fim previsto" e ritmo da barra)
// O coletor so manda batimento DEPOIS de cada codigo (nao durante a raspagem de um). Entao um codigo
// lento sozinho fica ~55s sem batimento — sem ter travado. O limite tem que ser MAIOR que isso pra nao
// dar falso "SEM RESPOSTA" (mordeu 2026-06-24: alarmou a 40s e a coleta terminou certinho logo depois).
const COLETOR_VIVO_SEG = 90;          // sem batimento ha mais que isso = coletor sumiu de verdade
function BannerColeta({ pendentes, total: totalProp, progresso, agora, inicioMs: inicioProp, onFechar, onCancelar }: { pendentes: { nome: string; solicitadaEm: string; nOfertas: number }[]; total: number; progresso?: { feitos: number; total: number; visto_ha_seg: number | null }; agora: number; inicioMs: number; onFechar: () => void; onCancelar: () => void }) {
  if (!pendentes.length) return null;
  // inicio no relogio do CLIENTE (do clique) — mesmo relogio do `agora`. Evita o skew VPS↔navegador
  // que deixava o decorrido negativo e travava a barra em 0% por ~1min. Fallback (F5 sem ref): banco.
  let inicioMs = inicioProp > 0 ? inicioProp : agora;
  if (inicioProp <= 0) { for (const p of pendentes) { const t = Date.parse(p.solicitadaEm); if (Number.isFinite(t) && t < inicioMs) inicioMs = t; } }

  const totalFamilias = Math.max(totalProp, pendentes.length, 1);
  const decorrido = Math.max(0, agora - inicioMs);
  const umaFamilia = totalFamilias <= 1;

  // batimento do coletor: vivo = reportou ha menos de COLETOR_VIVO_SEG. Enquanto vivo, sem alarme.
  const coletorVivo = progresso?.visto_ha_seg != null && progresso.visto_ha_seg < COLETOR_VIVO_SEG;
  const temProgReal = coletorVivo && (progresso?.total ?? 0) > 0;
  const feitos = progresso?.feitos ?? 0;
  const totalCod = progresso?.total ?? 0;

  // total de codigos do lote (pra estimar o fim): o que o coletor reporta, senao soma das ofertas.
  const codigosPend = pendentes.reduce((acc, p) => acc + Math.max(1, p.nOfertas), 0);
  const totalCodEstim = totalCod > 0 ? totalCod : codigosPend;
  const estimativaMs = Math.max(COLETA_POR_CODIGO_MS, totalCodEstim * COLETA_POR_CODIGO_MS);
  const fimEstimadoMs = inicioMs + estimativaMs;

  // SO alarma se o coletor sumiu (sem batimento por COLETOR_VIVO_SEG) E ja demos um tempo. Vivo nunca
  // alarma. O piso de decorrido cobre o arranque (1o codigo lento) sem alarmar cedo demais.
  const travou = !coletorVivo && decorrido > 60000;
  // a barra SEMPRE progride: avanca sozinha pelo tempo estimado (suave, sobe a cada seg) e DA SALTO
  // quando o coletor reporta codigo real. Pega o MAIOR dos dois. Capa em 95% ate terminar de fato.
  const timePct = Math.min(95, (decorrido / estimativaMs) * 100);
  const realPct = temProgReal ? (feitos / totalCod) * 100 : 0;
  const pct = Math.max(timePct, realPct);
  const indeterminado = false;

  return (
    // FLUTUA centralizado por cima da tela (nao empurra a lista). Sem transparencia (opaco).
    <div style={{
      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1200,
      width: 'min(680px, 64vw)', minWidth: 360,
      filter: 'drop-shadow(0 16px 40px rgba(0,0,0,.5))',
    }}>
      <ProgressoCoreSync
        titulo="ATUALIZAR PRECOS"
        alvo={umaFamilia ? (pendentes[0]?.nome.toUpperCase() ?? 'FORNECEDORES') : 'FORNECEDORES'}
        estado={travou ? 'travado' : 'progresso'}
        onFechar={onFechar}
        onCancelar={onCancelar}
        relogioMs={agora}
        inicioMs={inicioMs}
        fimEstimadoMs={fimEstimadoMs}
        pct={pct}
        indeterminado={indeterminado}
        rotuloBarra={travou ? 'SEM RESPOSTA' : (temProgReal ? `${feitos}/${totalCod} CODIGOS` : `${Math.round(pct)}%`)}
        statusTexto={travou ? 'SEM RESPOSTA' : 'COLETANDO // ATIVO'}
        rodapeEsq={umaFamilia ? undefined : <><b style={{ color: '#b6bdf2', fontWeight: 400 }}>{totalFamilias}</b> FAMILIAS</>}
        rodapeDir={temProgReal
          ? <>CODIGO <b style={{ color: '#b6bdf2', fontWeight: 400 }}>{feitos}</b> DE <b style={{ color: '#b6bdf2', fontWeight: 400 }}>{totalCod}</b></>
          : (umaFamilia ? <>AGUARDANDO O COLETOR</> : <><b style={{ color: '#b6bdf2', fontWeight: 400 }}>{totalFamilias}</b> FAMILIAS NA FILA</>)}
        aviso={travou
          ? <><b>O coletor parou de responder.</b> A janela <i>Atualizar Precos Martins</i> (ou <i>Tambasa</i>) pode ter fechado ou travado. Confira/reabra a janela — assim que ela voltar, ele retoma sozinho (o pedido fica na fila).</>
          : undefined}
      />
    </div>
  );
}

// ============================================================
// Card de progresso da busca (estado de espera). Quando o coletor ja reporta a pagina atual
// (progresso REAL, entregue pagina-a-pagina), a barra usa pagina/paginas. Enquanto nao chegou a
// 1a pagina, cai numa ESTIMATIVA pelo tempo decorrido (capa em ~92%). Restante idem.
// ============================================================
const BUSCA_ESTIMATIVA_MS = 75000; // duracao tipica de uma varredura (rotulada como estimativa)
const BUSCA_TRAVOU_MS = 150000;    // > 2,5 min sem responder = coletor provavelmente travou/fechou
// Mesma assinatura de antes — so troca o visual pro HUD unico ProgressoCoreSync. Inicio = agora -
// decorrido (o pai passa o decorrido); fim = inicio + estimativa. % = pagina/paginas (real) ou tempo.
function ProgressoBusca({ termo, decorridoMs, fase, pagina, paginas }: { termo?: string; decorridoMs: number; fase: 'aguardando' | 'varrendo'; pagina?: number | null; paginas?: number | null }) {
  const travou = decorridoMs > BUSCA_TRAVOU_MS;
  const temReal = fase === 'varrendo' && pagina != null && paginas != null && paginas > 0;
  const pct = temReal
    ? Math.min(96, Math.round((pagina! / paginas!) * 100))
    : Math.min(92, Math.round((decorridoMs / BUSCA_ESTIMATIVA_MS) * 100));
  const restanteSeg = Math.ceil(Math.max(0, BUSCA_ESTIMATIVA_MS - decorridoMs) / 1000);
  const inicioMs = Date.now() - decorridoMs;
  return (
    <div style={{ padding: '6px 2px' }}>
      <ProgressoCoreSync
        titulo="BUSCAR"
        alvo={termo ? termo.toUpperCase() : null}
        estado={travou ? 'travado' : 'progresso'}
        relogioMs={inicioMs + decorridoMs}
        inicioMs={inicioMs}
        fimEstimadoMs={inicioMs + BUSCA_ESTIMATIVA_MS}
        pct={pct}
        statusTexto={travou ? 'SEM RESPOSTA' : (fase === 'varrendo' ? 'VARRENDO PAGINAS' : 'AGUARDANDO COLETOR')}
        rodapeEsq={fase === 'varrendo' ? <>VARRENDO O CATALOGO MARTINS</> : <>PREPARANDO A BUSCA</>}
        rodapeDir={temReal
          ? <>PAGINA <b style={{ color: '#9fdcee', fontWeight: 400 }}>{pagina}</b> DE <b style={{ color: '#9fdcee', fontWeight: 400 }}>{paginas}</b></>
          : (restanteSeg > 0 ? <>RESTANTE: <b style={{ color: '#9fdcee', fontWeight: 400 }}>~{restanteSeg}s</b></> : <>CONCLUINDO…</>)}
        aviso={travou
          ? <><b>Esta demorando mais que o normal.</b> O coletor (janela <i>Atualizar Precos Martins</i>) pode ter travado ou fechado. Confira a janela e clique em <b>Buscar</b> de novo — uma busca nova cancela esta.</>
          : undefined}
      />
    </div>
  );
}

// O que o "Vincular" entrega pronto pro cadastro do catalogo. Uma perna so (a filial de GO): o item
// do catalogo e UM por empresa (DA-310) e ja contempla as duas filiais — a irma de fora entra pelo
// proprio modal, quando/se tiver oferta.
type PrefillCatalogo = {
  fornecedor_id?: number;
  codigo_fornecedor?: string | null;
  descricao_fornecedor?: string | null;
  marca?: string | null;
  quantidade_embalagem?: string | null;
};

// ============================================================
// Modal de resultados da busca por nome (descoberta de codigo).
// Estados: aguardando coletor (girando, com aviso se passar ~25s) | lista | vazio | erro.
// Cada linha: No catalogo (vincular) + Adicionar a familia (reaproveita "+ Adicionar codigo").
// Modal NUNCA fecha por overlay (so X/Cancelar/Esc).
// ============================================================
function ModalResultadosBusca({
  busca, familias, alvoFamilia, iniciadaEm, fornecedores, onFechar, onAdicionado,
}: {
  busca: BuscaApi | null;
  familias: FamiliaApi[];
  alvoFamilia: number | null;
  iniciadaEm: number;
  fornecedores: FornecedorApi[];
  onFechar: () => void;
  onAdicionado: () => void; // avisa o pai pra recarregar as familias em segundo plano
}) {
  const toast = useToast();
  // qual familia mirar ao adicionar: a alvo (busca aberta de uma familia) ou a escolhida no seletor.
  const [familiaSel, setFamiliaSel] = useState<number | null>(alvoFamilia ?? familias[0]?.monitor_item_id ?? null);
  // status por (familia + codigo) do botao "Adicionar a familia": salvando / ok / erro.
  const [statusAdd, setStatusAdd] = useState<Record<string, 'salvando' | 'ok' | 'erro'>>({});
  const keyAdd = (codigo: string) => `${familiaSel}::${codigo}`;
  // timeout: se passar ~25s sem resposta, mostra o aviso do coletor (nao gira pra sempre).
  const [estourou, setEstourou] = useState(false);
  // mapa codigo -> vinculo no catalogo (null = nao vinculado, undefined = ainda carregando).
  const [catalogoStatus, setCatalogoStatus] = useState<CatalogoStatusMap | null>(null);
  // prefill do cadastro do catalogo quando o usuario clica "Vincular" (null = cadastro fechado).
  const [vincularPrefill, setVincularPrefill] = useState<PrefillCatalogo | null>(null);
  // marcas pra tentar pre-preencher a marca a partir da descricao da Martins (melhor esforco).
  const [marcas, setMarcas] = useState<{ id: number; nome: string }[]>([]);
  // "relogio" que avanca a barra de progresso estimada enquanto a busca roda.
  const [agora, setAgora] = useState(() => Date.now());

  const pendente = busca?.status === 'pendente';   // ninguem pegou ainda (mostra aviso se demorar)
  const coletando = busca?.status === 'coletando'; // coletor varrendo as paginas (sem alarme falso)

  useEffect(() => {
    if (!pendente && !coletando) return;
    setAgora(Date.now());
    const id = setInterval(() => setAgora(Date.now()), 500);
    return () => clearInterval(id);
  }, [pendente, coletando]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { if (vincularPrefill) { setVincularPrefill(null); } else { onFechar(); } } }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar, vincularPrefill]);

  useEffect(() => {
    if (!pendente) { setEstourou(false); return; }
    const restante = Math.max(0, 25000 - (Date.now() - (iniciadaEm || Date.now())));
    const id = setTimeout(() => setEstourou(true), restante);
    return () => clearTimeout(id);
  }, [pendente, iniciadaEm]);

  useEffect(() => { void api.get<{ id: number; nome: string }[]>('/api/marcas').then(setMarcas).catch(() => {}); }, []);

  // So lista quem tem codigo: sem o codigo da Martins nao da pra vincular nem adicionar a familia.
  // O backend ja filtra na gravacao; aqui tambem, pra limpar resultado antigo sem refazer a busca.
  const resultados = (busca?.resultado ?? []).filter((r) => r.codigo && r.codigo.trim());
  const familiasOrd = familias;

  // re-consulta o mapa de vinculos (quando os resultados chegam, e apos salvar um vinculo).
  const recarregarStatus = useCallback(() => {
    const codigos = (busca?.resultado ?? []).map((r) => r.codigo).filter(Boolean);
    if (!codigos.length) return;
    void api.post<{ status: CatalogoStatusMap }>('/api/monitor-precos/busca/catalogo-status', {
      codigos,
      fornecedor: busca?.fornecedor || 'Martins',
    }).then((r) => setCatalogoStatus(r.status)).catch(() => {});
  }, [busca?.resultado, busca?.fornecedor]);

  // Busca o status do catalogo conforme os resultados chegam — inclusive durante o sweep
  // (parciais), pra coluna "No catalogo" ja ir preenchendo enquanto a busca continua.
  useEffect(() => {
    if (resultados.length === 0) return;
    recarregarStatus();
  }, [resultados.length, recarregarStatus]);

  // "Vincular": abre o cadastro REAL do catalogo pre-preenchido com os dados da Martins, pra revisar
  // e Salvar. Produto fica em branco (o usuario escolhe); a filial que vem pronta e a MARTINS GO —
  // a MG e a mesma EMPRESA e divide o item, entao nao ha mais "Fornecedor 2" pra amarrar (DA-310).
  // Marca + qtd por melhor esforco da descricao; PRECO em branco de proposito (so temos o preco de
  // vitrine, que NAO e o custo real do armazem — entra pela coleta).
  function abrirVinculo(codigo: string, descricao: string | null) {
    const acharForn = (alvo: string) => fornecedores.find((f) => (f.nome_fantasia || '').trim().toUpperCase() === alvo)?.id;
    const desc = (descricao || '').toUpperCase();
    const marca = marcas.find((m) => m.nome && desc.includes(m.nome.trim().toUpperCase()))?.nome ?? null;
    const metros = desc.match(/(\d{2,4})\s*M(?:TS|ETROS)?\b(?!M)/);
    setVincularPrefill({
      fornecedor_id: acharForn('MARTINS GO')
        ?? fornecedores.find((f) => (f.nome_fantasia || '').toUpperCase().includes('MARTINS'))?.id,
      codigo_fornecedor: codigo,
      descricao_fornecedor: descricao || null,
      marca,
      quantidade_embalagem: metros ? metros[1] : null,
    });
  }

  // "Adicionar a familia" SEM sair do modal: grava a oferta (Martins GO, metragem/marca da
  // descricao) E ja preenche a linha igual ao primeiro registro, gravando uma coleta com a
  // descricao + o preco que a propria busca mostra (o preco de vitrine e o "Pr. Forn." da linha;
  // o Custo sai dele menos o cupom, como em qualquer oferta). Sem preco na busca ("—"), enfileira
  // a coleta real pro coletor (ja aberto) trazer. Um "Atualizar" depois sempre re-coleta o fresco.
  async function adicionarDireto(item: BuscaResultadoItem) {
    const codigo = item.codigo;
    if (familiaSel == null || !codigo) return;
    const k = keyAdd(codigo);
    if (statusAdd[k] === 'salvando' || statusAdd[k] === 'ok') return;
    setStatusAdd((s) => ({ ...s, [k]: 'salvando' }));
    try {
      const acharForn = (alvo: string) => fornecedores.find((f) => (f.nome_fantasia || '').trim().toUpperCase() === alvo)?.id;
      const fornId = acharForn('MARTINS GO')
        ?? fornecedores.find((f) => (f.nome_fantasia || '').toUpperCase().includes('MARTINS'))?.id
        ?? fornecedores[0]?.id;
      if (!fornId) { setStatusAdd((s) => ({ ...s, [k]: 'erro' })); toast.erro('Nenhum fornecedor Martins cadastrado'); return; }
      const desc = (item.descricao || '').toUpperCase();
      const marca = marcas.find((mm) => mm.nome && desc.includes(mm.nome.trim().toUpperCase()))?.nome ?? null;
      const metros = desc.match(/(\d{2,4})\s*M(?:TS|ETROS)?\b(?!M)/);
      await api.post('/api/monitor-precos/ofertas', {
        monitor_item_id: familiaSel,
        fornecedor_id: fornId,
        // UF do card quando o vendedor e armazem oficial Martins (GO/MG); senao GO de partida.
        // Provisorio — a coleta por codigo ("Atualizar") mede e remede a UF pelo menor custo.
        uf: (item.uf || 'GO').toUpperCase(),
        metragem_embalagem: metros ? Number(metros[1]) : 100,
        marca,
        // O nome do card entra como CADASTRO, nao so como coleta (0246). Sem isso, o item
        // adicionado por uma busca SEM preco valido nascia mudo: o caminho abaixo so grava a
        // coleta quando ha preco, e sem coleta nao havia descricao em lugar nenhum.
        descricao: item.descricao || null,
      });
      if (item.preco != null) {
        // preenche na hora com o que a busca ja traz (mesma origem do primeiro registro).
        await api.post('/api/monitor-precos/coleta', {
          fornecedor: busca?.fornecedor || 'Martins',
          resultados: [{ codigo_fornecedor: codigo.trim(), preco_embalagem: item.preco, descricao: item.descricao ?? null, disponivel: true }],
        });
      } else {
        // busca sem preco: deixa o coletor (ja aberto) trazer o valor real.
        try { await api.post(`/api/monitor-precos/${familiaSel}/solicitar-coleta`, {}); } catch { /* nao bloqueia o add */ }
      }
      setStatusAdd((s) => ({ ...s, [k]: 'ok' }));
      onAdicionado();
    } catch (e) {
      setStatusAdd((s) => ({ ...s, [k]: 'erro' }));
      toast.erro('Falha ao adicionar: ' + (e as Error).message);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, paddingTop: '7vh' }}>
      <div className="modal-conteudo" style={{ background: 'var(--modal-bg, var(--panel-bg, #fff))', border: '1px solid var(--panel-border)', borderRadius: 13, width: 1204, maxWidth: '96vw', maxHeight: '84vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--panel-border)' }}>
          <h2 style={{ fontSize: 15, margin: 0, fontWeight: 700, fontFamily: 'var(--font-display)' }}>
            Resultados na Martins{busca?.termo ? <span style={{ color: 'var(--muted, #6b7280)', fontWeight: 400 }}> · "{busca.termo}"</span> : null}
          </h2>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn-fechar-modal" onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #6b7280)' }}><X size={18} /></button>
        </div>

        {/* Seletor de familia alvo (a busca pode ser global) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--panel-border)', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11.5, color: 'var(--muted, #6b7280)' }}>Adicionar o codigo escolhido na familia:</label>
          <select value={familiaSel ?? ''} onChange={(e) => setFamiliaSel(e.target.value ? Number(e.target.value) : null)}
            style={{ fontSize: 12, padding: '5px 9px', borderRadius: 7, minWidth: 220 }}>
            {familiasOrd.map((f) => (
              <option key={f.monitor_item_id} value={f.monitor_item_id}>{f.descricao || `Familia #${f.monitor_item_id}`}</option>
            ))}
          </select>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {coletando && resultados.length === 0 ? (
            <ProgressoBusca termo={busca?.termo} decorridoMs={Math.max(0, agora - (iniciadaEm || agora))} fase="varrendo"
              pagina={busca?.progresso_pagina} paginas={busca?.progresso_paginas} />
          ) : pendente ? (
            estourou ? (
              <div style={{ background: 'var(--positive-bg)', color: 'var(--positive-fg)', borderRadius: 9, padding: '14px 16px', fontSize: 13, lineHeight: 1.5 }}>
                <b>Nenhuma resposta ainda.</b><br />
                O coletor (janela <i>Atualizar Precos Martins</i>, modo <code>--watch</code>) precisa estar aberto e logado pra responder a busca. Abra-o e a tela atualiza sozinha.
              </div>
            ) : (
              <ProgressoBusca termo={busca?.termo} decorridoMs={Math.max(0, agora - (iniciadaEm || agora))} fase="aguardando" />
            )
          ) : busca?.status === 'erro' && resultados.length === 0 ? (
            <div style={{ background: 'var(--positive-bg)', color: 'var(--positive-fg)', borderRadius: 9, padding: '14px 16px', fontSize: 13, lineHeight: 1.5 }}>
              <b>A busca nao retornou.</b><br />
              Pode ter sido cancelada por uma busca nova, ou a raspagem falhou. Tente de novo; se persistir, rode o coletor com <code>FDC_DEBUG=1</code> pra calibrar.
            </div>
          ) : resultados.length === 0 ? (
            <div style={{ color: 'var(--muted, #6b7280)', fontSize: 13, padding: '10px 2px' }}>
              Nenhum produto encontrado pra esse termo na Martins.
            </div>
          ) : (
            <>
              {coletando ? (
                // varrendo COM parciais ja na tela: faixa viva no topo (a lista vai crescendo sozinha).
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: 'var(--accent-soft, rgba(0,0,0,0.05))', border: '1px solid var(--panel-border)', fontSize: 12 }}>
                  <Search size={14} className="animate-pulse" style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  <span>
                    Varrendo a Martins
                    {busca?.progresso_pagina != null && busca?.progresso_paginas != null
                      ? <> — pagina <span style={num}>{busca.progresso_pagina}</span> de <span style={num}>{busca.progresso_paginas}</span></>
                      : null}
                    {' · '}<b><span style={num}>{resultados.length}</span> itens ate agora</b>
                  </span>
                  <span style={{ flex: 1 }} />
                  <div className="fdc-barra-listrada" style={{ width: 90, height: 7, borderRadius: 999 }} />
                </div>
              ) : busca?.status === 'erro' ? (
                // travou/foi cancelada com parciais: fica o que veio (estilo alerta solido).
                <div style={{ marginBottom: 10, padding: '9px 12px', borderRadius: 8, background: '#fde68a', color: '#000', border: '1px solid #d97706', borderLeftWidth: 4, fontSize: 12, lineHeight: 1.45 }}>
                  <b>Busca interrompida.</b> Estes sao os <span style={num}>{resultados.length}</span> resultados coletados ate parar — pode usar, ou clicar em <b>Buscar</b> de novo pra varrer tudo.
                </div>
              ) : busca?.truncado ? (
                <div style={{ fontSize: 11.5, color: 'var(--muted, #6b7280)', marginBottom: 8 }}>
                  Mostrando os primeiros {resultados.length} resultados (havia mais).
                </div>
              ) : null}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Th align="left">Codigo</Th>
                    <Th align="left">Descricao</Th>
                    <Th>Preco vitrine</Th>
                    <Th align="left">No catalogo</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((r, i) => {
                    const vinculo = catalogoStatus ? catalogoStatus[r.codigo] : undefined;
                    const vinculado = vinculo != null;
                    return (
                      <Fragment key={(r.codigo || '') + i}>
                        <tr>
                          <td style={{ padding: '8px 12px', textAlign: 'left', borderTop: '1px solid var(--panel-border)', whiteSpace: 'nowrap' }}>
                            <span style={num}>{r.codigo || '—'}</span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'left', borderTop: '1px solid var(--panel-border)', fontSize: 12 }}>
                            {r.descricao || '—'}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', borderTop: '1px solid var(--panel-border)', ...num, whiteSpace: 'nowrap' }}>
                            {r.preco == null ? '—' : 'R$ ' + formatMoedaSemSimbolo(r.preco, 2)}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'left', borderTop: '1px solid var(--panel-border)', fontSize: 12, minWidth: 160 }}>
                            {catalogoStatus == null ? (
                              <span style={{ color: 'var(--muted, #6b7280)' }}>…</span>
                            ) : vinculado ? (
                              <span style={{ color: 'var(--muted, #6b7280)', fontSize: 11 }}>
                                {vinculo!.codigo_interno ? <span style={num}>{vinculo!.codigo_interno}</span> : null}
                                {vinculo!.codigo_interno && vinculo!.descricao ? ' · ' : null}
                                {vinculo!.descricao || (vinculo!.codigo_interno ? null : `#${vinculo!.produto_id}`)}
                              </span>
                            ) : (
                              <button type="button" className="btn-secundario"
                                onClick={() => abrirVinculo(r.codigo, r.descricao ?? null)}
                                style={{ fontSize: 11, padding: '3px 9px' }}>
                                Vincular
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', borderTop: '1px solid var(--panel-border)', whiteSpace: 'nowrap' }}>
                            {(() => {
                              const st = familiaSel != null ? statusAdd[keyAdd(r.codigo)] : undefined;
                              if (st === 'ok') {
                                return (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 6, background: 'var(--positive-bg)', color: 'var(--positive-fg)' }}>
                                    <Check size={13} /> adicionado
                                  </span>
                                );
                              }
                              return (
                                <button type="button" className="btn-primario"
                                  disabled={!r.codigo || familiaSel == null || st === 'salvando'}
                                  onClick={() => adicionarDireto(r)}
                                  style={{ fontSize: 11, padding: '4px 11px' }}>
                                  {st === 'salvando' ? 'Adicionando…' : 'Adicionar a familia'}
                                </button>
                              );
                            })()}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '11px 16px', borderTop: '1px solid var(--panel-border)' }}>
          <button type="button" className="btn-secundario" onClick={onFechar} style={{ padding: '7px 14px' }}>Fechar</button>
        </div>
      </div>

      {/* Cadastro REAL do catalogo (revisar + Salvar) — dentro do overlay (z-1000) pra ficar por cima. */}
      {vincularPrefill && (
        <ModalEditarCatalogoItem
          item={null}
          prefill={vincularPrefill}
          onFechar={() => setVincularPrefill(null)}
          onSalvo={() => { setVincularPrefill(null); recarregarStatus(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Form inline de oferta (Adicionar codigo / Editar oferta).
// Segue "+ Adicionar estilo caixa": bloco que empilha; nao fecha modal por overlay (nao e modal).
// ============================================================
// Campo de MARCA com busca no cadastro (marcas). ComboBox: digita e filtra as marcas cadastradas;
// botao "+" cadastra marca nova (modal rapido -> /api/marcas). Usa position:fixed (nao e cortado pelo
// overflow:hidden do <details> da familia — ver regra de dropdown do CLAUDE.md). Mesmo padrao do Catalogo.
function CampoMarca({ value, onChange, wrapperStyle }: {
  value: string;
  onChange: (v: string) => void;
  wrapperStyle?: CSSProperties;
}) {
  const [marcas, setMarcas] = useState<{ id: number; nome: string }[]>([]);
  const [mostrarNovaMarca, setMostrarNovaMarca] = useState(false);
  useEffect(() => { void api.get<{ id: number; nome: string }[]>('/api/marcas').then(setMarcas).catch(() => {}); }, []);
  // opcoes = marcas do cadastro + a marca atual (se for texto livre fora do cadastro, nao some no edit).
  const opcoes = useMemo(() => {
    const base = marcas.map((mm) => ({ value: mm.nome, label: mm.nome }));
    if (value && !marcas.some((mm) => mm.nome === value)) base.unshift({ value, label: value });
    return base;
  }, [marcas, value]);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', ...wrapperStyle }}>
      {/* 13px com `!`: o .combo-input do styles.css crava 14px, e utility sem `!` depende da ordem do CSS gerado. */}
      <div style={{ flex: 1, minWidth: 0, '--input-fg': '#000' } as CSSProperties}>
        {/* Mesma altura dos outros campos do form (pedido dele, 08/10/2026: 28,9px, a do Fornecedor) —
            o .combo-input traz 8px de padding e min-height de 36px, os dois precisam ceder. */}
        <ComboBox opcoes={opcoes} value={value} onChange={(v) => onChange(String(v))} placeholder="Buscar marca..." className="!text-[13px] !py-[4.2px] !min-h-[28.9px] !h-[28.9px]" />
      </div>
      <button type="button" title="Cadastrar nova marca" onClick={() => setMostrarNovaMarca(true)}
        style={{ flexShrink: 0, height: 28.9, boxSizing: 'border-box', padding: '0 9px', borderRadius: 7, border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--panel-fg, inherit)', cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
        <Plus size={14} />
      </button>
      {mostrarNovaMarca && (
        <ModalCadastroRapido
          titulo="Nova Marca"
          endpoint="/api/marcas"
          campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
          onSalvo={(novo: { id: number; nome: string }) => {
            setMarcas((prev) => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
            onChange(novo.nome);
            setMostrarNovaMarca(false);
          }}
          onFechar={() => setMostrarNovaMarca(false)}
        />
      )}
    </div>
  );
}

// Posicao de lista de busca em `position: fixed` ancorada num input. Abre pra CIMA quando embaixo nao
// cabe (pedido dele, 08/10/2026, com print: o form de codigo fica no pe da tela e a lista saia pela
// borda — o antigo `max(160, ...)` forcava 160px mesmo com 60 sobrando, e fixed nao rola a pagina).
type PosLista = { left: number; width: number; top?: number; bottom?: number; maxHeight: number };
function posicaoLista(r: DOMRect): PosLista {
  const abaixo = window.innerHeight - r.bottom - 10;
  const acima = r.top - 10;
  const width = Math.min(Math.max(r.width, 340), window.innerWidth - 20);
  // Campo colado na borda direita (Produto interno do form da oferta): a lista ancorada no
  // left do campo passava da janela e cortava a descricao. Recua pra caber.
  const left = Math.max(10, Math.min(r.left, window.innerWidth - width - 10));
  if (abaixo >= 220 || abaixo >= acima) return { left, width, top: r.bottom + 2, maxHeight: Math.min(360, abaixo) };
  return { left, width, bottom: window.innerHeight - r.top + 2, maxHeight: Math.min(360, acima) };
}
// Quantos produtos a busca mostra. Era 10 e cortava calado: "ARIA" casa 41 no cadastro (medido
// 08/10/2026) e o resto nao aparecia por mais que se rolasse. A rota devolve ate 500.
const MAX_RESULTADOS_PRODUTO = 60;

// Campo "Produto interno" do form da oferta — o de-para codigo do fornecedor -> nosso produto.
// Busca por codigo OU descricao no `GET /api/produtos` (que ja ranqueia prefixo antes de "contem",
// DA-389). Dropdown em `position: fixed` medido por getBoundingClientRect: o form vive dentro do
// <details> da familia, que tem overflow — um dropdown absoluto seria cortado.
// Em BRANCO e resposta legitima (produto que a loja nao tem), entao nada aqui e obrigatorio.
function CampoProdutoInterno({
  produtoId, produtoRotulo, onEscolher, onLimpar,
}: {
  produtoId: number | null;
  produtoRotulo: string | null;
  onEscolher: (p: ProdutoBusca) => void;
  onLimpar: () => void;
}) {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ProdutoBusca[]>([]);
  const [pos, setPos] = useState<PosLista | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (busca.trim().length < 2) { setResultados([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get<ProdutoBusca[]>('/api/produtos', { query: { q: busca, com_inativos: '1' } });
        setResultados((r || []).slice(0, MAX_RESULTADOS_PRODUTO));
      } catch { setResultados([]); }
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [busca]);

  useEffect(() => {
    if (resultados.length === 0) { setPos(null); return; }
    const medir = () => {
      const el = inputRef.current;
      if (!el) return;
      setPos(posicaoLista(el.getBoundingClientRect()));
    };
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => { window.removeEventListener('scroll', medir, true); window.removeEventListener('resize', medir); };
  }, [resultados.length]);

  const dialog = useDialog();
  function escolher(p: ProdutoBusca) {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    onEscolher(p);
    setBusca('');
    setResultados([]);
  }
  const teclado = useListaTeclado(resultados, escolher);

  const campo = { padding: '5px 9px', fontSize: 13, borderRadius: 7, '--input-fg': '#000' } as CSSProperties;

  // Ja vinculado: mostra o que esta valendo (com o X pra desvincular), nao um campo de busca vazio.
  if (produtoId) {
    return (
      <div style={{ ...campo, width: 210, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--input-bg)', border: '1px solid var(--input-border)', height: 28.9, boxSizing: 'border-box', paddingTop: 0, paddingBottom: 0 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--input-fg)' }}>
          {produtoRotulo ?? `#${produtoId}`}
        </span>
        <button type="button" onClick={onLimpar} title="Desvincular"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 0, lineHeight: 1 }}>
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        onKeyDown={teclado.onKeyDown}
        placeholder="codigo ou descricao..."
        style={{ ...campo, width: 210 } as CSSProperties}
      />
      {pos && resultados.length > 0 && (
        // <div>, nao <ul>: o listaRef do useListaTeclado e tipado como HTMLDivElement (e o scroll
        // automatico do cursor mede o container, nao a semantica da lista).
        <div ref={teclado.listaRef}
          style={{
            position: 'fixed', ...pos, zIndex: 1100, overflowY: 'auto', margin: 0, padding: 4, listStyle: 'none',
            background: 'var(--popup-bg, #fff)', color: 'var(--popup-fg)',
            border: '1px solid var(--popup-border, #d1d5db)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.18)',
          }}>
          {resultados.map((p, i) => (
            <button key={p.id} type="button"
              onMouseEnter={() => teclado.setHighlight(i)}
              onClick={() => escolher(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '4px 8px', fontSize: 12,
                borderRadius: 6, border: 'none', cursor: 'pointer',
                background: teclado.highlight === i ? 'var(--primary-soft, #e1ecfd)' : 'transparent',
                color: 'inherit',
              }}>
              <MiniFotoBusca url={p.foto_url} />
              <span>{produtoInativo(p) && <SeloInativo />}<b style={{ fontFamily: 'var(--font-mono)' }}>{p.codigo_interno}</b> {p.descricao}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function OfertaForm({
  fornecedores, inicial, fornecedorPadraoId, codigoInicial, produtoPadrao, onSalvar, onCancelar,
}: {
  fornecedores: FornecedorApi[];
  inicial?: OfertaApi;
  fornecedorPadraoId?: number; // fornecedor herdado da familia (o badge do cabecalho) pra codigo novo
  codigoInicial?: string; // codigo pre-preenchido vindo da busca por nome
  produtoPadrao?: FamiliaApi['produto']; // vinculo da familia — padrao do Produto interno no codigo novo
  onSalvar: (payload: Record<string, unknown>) => void | Promise<void>;
  onCancelar: () => void;
}) {
  const nomeForn = (f: FornecedorApi) => (f.nome_fantasia || f.razao_social || `#${f.id}`);
  // Fornecedor VOLTOU a ser escolhido por codigo (Tambasa, 23/09/2026): a mesma familia compara
  // Martins e Tambasa, entao o codigo novo precisa dizer de quem e. Edicao preserva o da oferta (o
  // codigo so existe naquele site); codigo novo nasce com o da familia quando ela tem um so e, na
  // falta, com a Martins — e o combo deixa trocar.
  const [fornecedorId, setFornecedorId] = useState<number>(() => inicial
    ? (fornecedores.find((f) => nomeForn(f) === inicial.fornecedor_nome)?.id ?? inicial.fornecedor_id)
    : (fornecedorPadraoId ?? fornecedores.find((f) => nomeForn(f).toLowerCase().includes('martins'))?.id ?? fornecedores[0]?.id ?? 0));

  const [codigo, setCodigo] = useState(inicial?.codigo_fornecedor ?? codigoInicial ?? '');
  const [metragem, setMetragem] = useState(String(inicial?.qtd ?? 100));
  const [marca, setMarca] = useState(inicial?.marca && inicial.marca !== '—' ? inicial.marca : '');
  // Nasce com o que a LINHA exibe hoje, nao so com a ja digitada: abrir o lapis numa linha que
  // mostra o nome vindo da coleta e encontrar o campo vazio leria como "nao ha descricao". Assim,
  // salvar promove aquele nome a cadastro — que e justamente o que trava a perda na proxima coleta.
  const [descricao, setDescricao] = useState(inicial?.descricao ?? inicial?.descricao_fornecedor ?? '');
  // Preco digitado (0289). ⛔ Nasce com o JA DIGITADO ou VAZIO — nunca com a ultima leitura do robo,
  // que e o contrario do que a descricao faz logo acima. La o pre-preenchimento promove um nome bom
  // a cadastro; aqui promoveria um preco de semanas atras a "digitado hoje", e ele iria pra media
  // da familia sem ninguem ter conferido. O lastro aparece como LEGENDA embaixo do campo, onde
  // informa sem gravar.
  const [precoManual, setPrecoManual] = useState(
    inicial?.preco_manual != null ? formatMoedaSemSimbolo(inicial.preco_manual, 2) : '',
  );
  // De-para deste codigo. Codigo novo nasce com o vinculo da FAMILIA (pedido dele, 07/10/2026) — a
  // [DA-477] nascia vazio porque a familia agrupa por bitola e a cor muda de codigo pra codigo; o
  // padrao fica a vista no campo, com o X pra desvincular quando o codigo for de outra cor.
  const padraoNovo = !inicial && produtoPadrao ? produtoPadrao : null;
  const [produtoId, setProdutoId] = useState<number | null>(inicial ? inicial.produto_id ?? null : padraoNovo?.id ?? null);
  const [produtoRotulo, setProdutoRotulo] = useState<string | null>(
    inicial
      ? (inicial.produto_codigo ? `${inicial.produto_codigo} ${inicial.produto_descricao ?? ''}`.trim() : null)
      : (padraoNovo ? `${padraoNovo.codigo_interno ?? `#${padraoNovo.id}`} ${padraoNovo.descricao ?? ''}`.trim() : null),
  );
  // UF escolhida a mao (pedido dele, 12/09): quando o robo nao acha o codigo nao mede armazem nenhum,
  // e o preco digitado acima entrava na UF velha — preco de MG contado como GO, sem DIFAL. A coleta
  // que NAO acha o codigo nao toca na UF; a que acha remede e sobrescreve, junto com o preco digitado
  // perdendo a vez ([DA-204]). So GO/MG: sao os dois armazens que o coletor conhece.
  const [uf, setUf] = useState((inicial?.uf || 'GO').toUpperCase() === 'MG' ? 'MG' : 'GO');
  const [salvando, setSalvando] = useState(false);
  // [DA-804] Tramontina: o robo le o a vista do SITE, que vende a PECA (qtd 1), e o custo e esse
  // preco x fator. Fator PROPRIO da linha (vazio = o da caixa no topo), so pra quem foge da regra.
  // Desde a [DA-804] p.7 ele NAO digita o fator: digita o PRECO DO REPRESENTANTE — o da CAIXA da
  // proposta, com a Qtd ao lado (decisao dele, 09/10) — e o servidor calcula fator = representante /
  // qtd / ultima leitura do site (o site vende a PECA). A tela so mostra a previa.
  const ehTramontina = nomeForn(fornecedores.find((f) => f.id === fornecedorId) ?? { id: 0, razao_social: null, nome_fantasia: null, uf: null, cpf_cnpj: null })
    .toUpperCase().includes('TRAMONTINA');
  const [precoRep, setPrecoRep] = useState(
    inicial?.preco_representante != null ? formatMoedaSemSimbolo(inicial.preco_representante, 4) : '',
  );
  // Previa da conta que o servidor vai fazer — mesma peca do shared, contra a mesma leitura do site.
  const siteRef = inicial?.ultimo_preco_coletado ?? null;
  const siteRefEm = inicial?.ultimo_preco_coletado_em ?? null;
  const siteDeHoje = siteRefEm != null && new Date(siteRefEm).toDateString() === new Date().toDateString();
  const repPrevia = {
    valor: parsePrecoBR(precoRep),
    fator: fatorDoRepresentante(parsePrecoBR(precoRep), siteRef, Number(String(metragem).replace(',', '.'))),
  };
  // Codigo NOVO da Tramontina nasce com qtd 1 (o 100 do form e o rolo de cabo da Martins). So troca
  // o valor intocado — o que ele digitou fica.
  useEffect(() => {
    if (!inicial && ehTramontina) setMetragem((v) => (v === '100' ? '1' : v));
  }, [ehTramontina, inicial]);
  // A Tramontina so vende a marca dela (pedido dele, 09/10/2026): Marca vazia ja vem TRAMONTINA.
  // So preenche o VAZIO; e se ele trocar o fornecedor de volta, desfaz o que foi posto aqui
  // (nunca o que ele escolheu).
  const marcaAutoRef = useRef(false);
  useEffect(() => {
    if (ehTramontina) {
      setMarca((v) => { if (v.trim()) return v; marcaAutoRef.current = true; return 'TRAMONTINA'; });
    } else if (marcaAutoRef.current) {
      marcaAutoRef.current = false;
      setMarca((v) => (v === 'TRAMONTINA' ? '' : v));
    }
  }, [ehTramontina]);

  // Abriu o form = ja pode digitar o codigo. `preventScroll` porque quem rola a tela e o
  // scrollIntoView suave la do bloco da familia — o foco nao pode disputar com ele.
  const codigoRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      codigoRef.current?.focus({ preventScroll: true });
      codigoRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  async function submit() {
    if (!codigo.trim()) return;
    if (!fornecedorId) return;
    const mt = Number(String(metragem).replace(',', '.'));
    // So manda o preco do representante quando ele MUDOU: salvar o lapis pra trocar a marca nao pode
    // recalcular o fator contra o site de hoje sem ninguem ter pedido.
    const repNum = parsePrecoBR(precoRep);
    const mexeuNoRep = ehTramontina && (repNum ?? null) !== (inicial?.preco_representante ?? null);
    // Sem leitura do site o preco entra pendente (o fator sai na 1a leitura); com leitura, a conta
    // tem que fechar aqui — a legenda ja diz o motivo.
    if (mexeuNoRep && repNum != null && siteRef != null && repPrevia.fator == null) return;
    setSalvando(true);
    await onSalvar({
      codigo_fornecedor: codigo.trim(),
      fornecedor_id: fornecedorId,
      // A proxima coleta que achar o codigo remede e sobrescreve (menor custo entre Hidrolandia/GO e
      // Uberlandia/MG) — o que vai daqui vale enquanto o robo nao mede.
      uf,
      metragem_embalagem: Number.isFinite(mt) && mt > 0 ? mt : 100,
      marca: marca.trim() || null,
      // Vai SEMPRE, inclusive como null — mesma razao do produto_id logo abaixo: e a presenca da
      // chave que autoriza o backend a mexer, e sem ela apagar a descricao pela tela seria impossivel.
      descricao: descricao.trim() || null,
      // Idem: campo esvaziado tem que chegar como null explicito, senao nao ha como TIRAR o preco
      // digitado depois que o robo voltar a achar o codigo.
      preco_manual: parsePrecoBR(precoManual),
      // Vai SEMPRE, inclusive como null: e a presenca da chave que diz ao backend "mexi no
      // vinculo" — sem ela, o COALESCE do PUT preservaria o valor antigo e desvincular seria
      // impossivel pela tela.
      produto_id: produtoId,
      // [DA-804] p.7 — null explicito apaga (a linha volta pro fator do topo).
      ...(mexeuNoRep ? { preco_representante: repNum } : {}),
    });
    setSalvando(false);
  }

  // 13px e preto (pedido dele, 08/10/2026). A cor do input vai pela VARIAVEL: o styles.css forca
  // `color: var(--input-fg) !important` em todo input/select, e cor inline nao vence.
  const campo = { padding: '5px 9px', fontSize: 13, borderRadius: 7, '--input-fg': '#000' } as CSSProperties;
  const lbl: CSSProperties = { fontSize: 13, textTransform: 'uppercase', letterSpacing: '.3px', color: '#000', display: 'block', marginBottom: 2 };

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px 14px' }}>
      <div>
        <label style={lbl}>Fornecedor</label>
        <select
          value={fornecedorId || ''}
          onChange={(e) => setFornecedorId(Number(e.target.value))}
          disabled={!!inicial}
          title={inicial
            ? 'O codigo e daquele site — pra trocar de fornecedor, cadastre o codigo do outro como linha nova.'
            : 'De qual site e este codigo. So aparecem os fornecedores que tem robo de coleta.'}
          style={{ ...campo, width: 96 } as CSSProperties}
        >
          {fornecedores.map((f) => <option key={f.id} value={f.id}>{nomeForn(f).toUpperCase()}</option>)}
        </select>
      </div>
      <div>
        <label style={lbl}>Cod Fornec</label>
        <UpperInput ref={codigoRef} value={codigo} onChange={setCodigo} style={{ ...campo, width: 96 } as CSSProperties} placeholder="ex: 4200266" />
      </div>
      <div>
        <label style={lbl}>UF</label>
        <select
          value={uf}
          onChange={(e) => setUf(e.target.value)}
          title="MARTINS: GO = Hidrolandia, MG = Uberlandia (entra o DIFAL) — o robo mede o armazem mais barato e troca sozinho. TAMBASA: o site mostra so o preco GO, entao a linha fica GO."
          style={{ ...campo, width: 62, fontFamily: 'var(--font-mono)' } as CSSProperties}
        >
          <option value="GO">GO</option>
          <option value="MG">MG</option>
        </select>
      </div>
      <div>
        <label style={lbl}>Qtd</label>
        <input value={metragem} onChange={(e) => setMetragem(e.target.value)} inputMode="decimal"
          style={{ ...campo, width: 64, fontFamily: 'var(--font-mono)', textAlign: 'right' } as CSSProperties} />
      </div>
      {/* Preco digitado (0289). Fica colado na Qtd. emb. de proposito: os dois descrevem a MESMA
          embalagem, e o custo unitario e a divisao de um pelo outro. A legenda do lastro vai em
          `position: absolute` pra nao crescer o bloco — o form alinha por baixo (`flex-end`), entao
          um bloco mais alto que os vizinhos desalinha a fileira inteira de campos. */}
      <div style={{ position: 'relative' }}>
        <label style={lbl}>PR FORNEC</label>
        <input
          value={precoManual}
          onChange={(e) => setPrecoManual(e.target.value)}
          onBlur={() => { const n = parsePrecoBR(precoManual); setPrecoManual(n == null ? '' : formatMoedaSemSimbolo(n, 2)); }}
          inputMode="decimal"
          placeholder="sem leitura"
          title="Preco do fornecedor (R$): o preco da embalagem cheia, como esta no site do fornecedor. So quando o robo nao acha: vale enquanto a coleta nao trouxer preco; assim que o robo conseguir ler o codigo de novo, o dele volta a mandar."
          // Rotulo curto (pedido dele, 08/10/2026: o form inteiro numa linha so) — a largura acompanha
          // o rotulo; a legenda de baixo e absolute e pode passar da borda sem empurrar nada.
          style={{ ...campo, width: 104, fontFamily: 'var(--font-mono)', textAlign: 'right' } as CSSProperties}
        />
        {inicial?.ultimo_preco_coletado != null && (
          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 3, fontSize: 9.5, lineHeight: 1.3, whiteSpace: 'nowrap', color: 'var(--muted, #6b7280)' }}>
            {/* "coletado" e a palavra que a COLUNA da tabela ja usa — vocabulario da casa em vez de
                uma frase mais longa dizendo a mesma coisa, e sobra largura pro valor de 4 digitos. */}
            coletado <b>{formatMoedaSemSimbolo(inicial.ultimo_preco_coletado, 2)}</b>
            {inicial.ultimo_preco_coletado_em
              ? ` em ${new Date(inicial.ultimo_preco_coletado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
              : ''}
          </div>
        )}
      </div>
      {ehTramontina && (
        <div style={{ position: 'relative' }}>
          <label style={lbl}>PR REPRES</label>
          <input
            value={precoRep}
            onChange={(e) => setPrecoRep(e.target.value)}
            onBlur={() => { const n = parsePrecoBR(precoRep); setPrecoRep(n == null ? '' : formatMoedaSemSimbolo(n, 4)); }}
            inputMode="decimal"
            placeholder="da proposta"
            title="Preco do representante (R$): o preco da EMBALAGEM na proposta (a caixa com a Qtd ao lado). O sistema divide pela Qtd e calcula o fator = preco por peca / preco a vista do site (que vende a peca), e a linha passa a seguir o site com esse fator. Se o site nao tem o codigo, o custo e o proprio preco por peca. Vazio = usa o fator da Tramontina da caixa no topo."
            style={{ ...campo, width: 104, fontFamily: 'var(--font-mono)', textAlign: 'right' } as CSSProperties}
          />
          {/* A conta, ao vivo, embaixo do campo. Fora do fluxo (absolute) pelo mesmo motivo da legenda
              do preco ao lado: o form alinha por baixo e um bloco mais alto desalinha a fileira. */}
          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 3, fontSize: 10, lineHeight: 1.3, whiteSpace: 'nowrap', color: '#000' }}>
            {siteRef == null ? (
              <span>{repPrevia.valor == null
                ? 'vazio = fator do topo'
                : 'sem leitura do site: o custo e este preco ÷ Qtd'}</span>
            ) : repPrevia.valor == null ? (
              <span>{inicial?.fator_site_pct != null
                ? `fator da linha ${inicial.fator_site_pct.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}% · vazio = o do topo`
                : 'vazio = fator do topo'}</span>
            ) : repPrevia.fator == null ? (
              <span style={{ background: '#f97316', fontWeight: 600, borderRadius: 4, padding: '0 5px' }}>
                nao fecha com o site {formatMoedaSemSimbolo(siteRef, 2)} — confira
              </span>
            ) : (
              <span>
                fator <b>{repPrevia.fator.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}%</b>
                {' · site '}{formatMoedaSemSimbolo(siteRef, 2)}{' lido '}
                {/* So vale com os dois precos do MESMO dia: leitura antiga fica destacada. */}
                <span style={siteDeHoje ? undefined : { background: '#f97316', fontWeight: 600, borderRadius: 4, padding: '0 4px' }}
                  title={siteDeHoje ? undefined : 'A leitura do site nao e de hoje — atualize a linha antes de salvar, senao o fator compara precos de dias diferentes.'}>
                  {siteRefEm ? new Date(siteRefEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
      <div>
        <label style={lbl}>Marca (opcional)</label>
        <CampoMarca value={marca} onChange={setMarca} wrapperStyle={{ width: 190 }} />
      </div>
      {/* Larguras pelo ROTULO (pedido dele, 08/10/2026: o form numa linha so). Medido na janela dele
          (1536 CSS, zoom 125%): com os botoes na linha de baixo sobram 1310px pros campos, e a
          Descricao fica com ~384 (o rotulo pede 259). Mexer numa largura daqui pede refazer essa conta. */}
      <div style={{ flex: '1 1 260px', minWidth: 260 }}>
        <label style={lbl}>Descricao (o nome no fornecedor)</label>
        {/* `textTransform: none` sai do uppercase global de propósito: isto e o nome do produto no
            site do fornecedor ("Cabo Flexivel Corfio 6mmx100m Rolo 750V Verde"), do mesmo tipo dos
            textos de WhatsApp — a caixa importa, e o que ja veio de coleta e misto. */}
        <input value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={500}
          placeholder="ex: Cabo Flexivel Corfio 6mmx100m Rolo 750V Verde"
          title="O que voce digitar aqui manda: nenhuma atualizacao de preco sobrescreve. Vazio = usa o nome da ultima coleta com o codigo confirmado."
          style={{ ...campo, width: '100%', textTransform: 'none' } as CSSProperties} />
      </div>
      <div>
        <label style={lbl}>Produto interno (opcional)</label>
        <CampoProdutoInterno
          produtoId={produtoId}
          produtoRotulo={produtoRotulo}
          onEscolher={(p) => { setProdutoId(p.id); setProdutoRotulo(`${p.codigo_interno} ${p.descricao}`); }}
          onLimpar={() => { setProdutoId(null); setProdutoRotulo(null); }}
        />
      </div>
      {/* Botoes na linha de BAIXO (pedido dele, 08/10/2026): `flexBasis 100%` forca a quebra e
          devolve o espaco aos campos — foi o que deixou a Marca voltar aos 190. */}
      <div style={{ display: 'flex', gap: 7, flexBasis: '100%' }}>
        <button type="button" className="btn-primario" onClick={submit} disabled={salvando || !codigo.trim()} style={{ fontSize: 13, padding: '6px 14px' }}>
          {inicial ? 'Salvar' : 'Adicionar'}
        </button>
        <button type="button" className="btn-secundario" onClick={onCancelar} style={{ fontSize: 13, padding: '6px 12px', color: '#000' }}>Cancelar</button>
      </div>
    </div>
  );
}

// ============================================================
// Modal "Adicionar familia": NOME + MARKUP, so isso. A familia e o rotulo sob o qual a gente compara
// os codigos (migration 0130); codigo do fornecedor, qtd. da embalagem e marca sao atributos da
// OFERTA e entram pelo "+ Adicionar codigo" do bloco — nao aqui.
// Modal NUNCA fecha por overlay (so X/Cancelar/Esc).
// ============================================================
function ModalAdicionarFamilia({ grupoId, onFechar, onCriado }: {
  grupoId?: number | null;
  onFechar: () => void;
  onCriado: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [markup, setMarkup] = useState('30');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onFechar(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  async function criar() {
    const n = nome.trim();
    if (!n) { toast.erro('Informe o nome da familia'); return; }
    const mk = Number(String(markup).replace(',', '.'));
    if (!Number.isFinite(mk)) { toast.erro('Markup invalido'); return; }
    setSalvando(true);
    try {
      await api.post('/api/monitor-precos/itens', {
        nome: n, markup: mk / 100, ...(grupoId != null ? { grupo_id: grupoId } : {}),
      });
      await onCriado();
    } catch (e) { toast.erro((e as Error).message); }
    finally { setSalvando(false); }
  }

  const lbl: CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--muted, #6b7280)', display: 'block', marginBottom: 4 };
  const campo: CSSProperties = { width: '100%', padding: '8px 11px', borderRadius: 8 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, paddingTop: '8vh' }}>
      <div className="modal-conteudo" style={{ background: 'var(--modal-bg, var(--panel-bg, #fff))', border: '1px solid var(--panel-border)', borderRadius: 13, width: 460, maxWidth: '94vw', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid var(--panel-border)' }}>
          <h2 style={{ fontSize: 15, margin: 0, fontWeight: 700, fontFamily: 'var(--font-display)' }}>Adicionar familia ao monitor</h2>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn-fechar-modal" onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #6b7280)' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>Nome da familia</label>
            <UpperInput value={nome} onChange={setNome} autoFocus placeholder="ex: FLEX 1,5MM AZUL"
              onKeyDown={(e) => { if (e.key === 'Enter') void criar(); }} style={campo as CSSProperties} />
          </div>
          <div style={{ width: 110 }}>
            <label style={lbl}>Markup %</label>
            <input value={markup} onChange={(e) => setMarkup(e.target.value)} inputMode="decimal"
              style={{ ...campo, textAlign: 'right', fontFamily: 'var(--font-mono)' } as CSSProperties} />
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted, #6b7280)', margin: 0 }}>
            A familia nasce vazia — os codigos dos fornecedores entram pelo “+ Adicionar codigo” do bloco.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
            <button type="button" className="btn-secundario" onClick={onFechar} style={{ padding: '7px 14px' }}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={criar} disabled={salvando || !nome.trim()} style={{ padding: '7px 16px' }}>Adicionar familia</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Modal "Criar grupo": campo nome → POST /grupos.
// Modal NUNCA fecha por overlay (so X/Cancelar/Esc).
// ============================================================
// Mover um codigo pra outra familia. O combo abre VAZIO (nada escolhido por ele) e a familia onde o
// codigo ja esta fica na lista, cinza, com o motivo — ausencia nao se distingue de inexistencia (DA-468).
function ModalMoverOferta({ oferta, familiaAtualId, familias, grupos, onFechar, onMovido }: {
  oferta: OfertaApi; familiaAtualId: number; familias: FamiliaApi[]; grupos: GrupoApi[];
  onFechar: () => void; onMovido: (nomeDestino: string) => void | Promise<void>;
}) {
  const toast = useToast();
  const [destino, setDestino] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const nomeGrupo = useMemo(() => new Map(grupos.map((g) => [g.grupo_id, g.nome])), [grupos]);
  const opcoes = useMemo(() => familias
    .map((f) => ({
      value: f.monitor_item_id,
      label: f.grupo_id != null && nomeGrupo.get(f.grupo_id) ? `${f.descricao}  (grupo ${nomeGrupo.get(f.grupo_id)})` : f.descricao,
      desabilitada: f.monitor_item_id === familiaAtualId,
      nota: f.monitor_item_id === familiaAtualId ? 'esta aqui' : undefined,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')), [familias, familiaAtualId, nomeGrupo]);
  const atual = familias.find((f) => f.monitor_item_id === familiaAtualId);

  async function mover() {
    if (destino == null) return;
    setSalvando(true);
    try {
      await api.put(`/api/monitor-precos/ofertas/${oferta.oferta_id}/mover`, { monitor_item_id: destino });
      await onMovido(familias.find((f) => f.monitor_item_id === destino)?.descricao ?? 'a outra familia');
    } catch (e) { toast.erro((e as Error).message); }
    finally { setSalvando(false); }
  }

  return (
    <Modal aberto onFechar={onFechar} titulo="Mover para outra familia" largura={520} compacto
      rodape={<>
        <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
        <button type="button" className="btn-primario" onClick={mover} disabled={salvando || destino == null}>
          {salvando ? 'Movendo...' : 'Mover'}
        </button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          <div><b style={num}>{oferta.codigo_fornecedor}</b> · {oferta.fornecedor_nome}</div>
          {oferta.descricao_fornecedor && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{oferta.descricao_fornecedor}</div>}
          <div style={{ marginTop: 6, fontSize: 12 }}>Esta hoje em: <b>{atual?.descricao ?? '—'}</b></div>
        </div>
        <div>
          <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Familia de destino</label>
          <ComboBox opcoes={opcoes} value={destino ?? ''} onChange={(v) => setDestino(v === '' ? null : Number(v))} placeholder="Escolha a familia..." />
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
          {destino == null
            ? 'Escolha a familia de destino para liberar o Mover.'
            : 'O historico de precos, a foto e o vinculo com o Catalogo vao junto com o codigo.'}
        </p>
      </div>
    </Modal>
  );
}

function ModalAdicionarGrupo({ onFechar, onCriado }: { onFechar: () => void; onCriado: () => void | Promise<void> }) {
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onFechar(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  async function criar() {
    const n = nome.trim();
    if (!n) { toast.erro('Informe o nome do grupo'); return; }
    setSalvando(true);
    try {
      await api.post('/api/monitor-precos/grupos', { nome: n });
      await onCriado();
    } catch (e) { toast.erro((e as Error).message); }
    finally { setSalvando(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, paddingTop: '8vh' }}>
      <div className="modal-conteudo" style={{ background: 'var(--modal-bg, var(--panel-bg, #fff))', border: '1px solid var(--panel-border)', borderRadius: 13, width: 400, maxWidth: '94vw', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid var(--panel-border)' }}>
          <h2 style={{ fontSize: 15, margin: 0, fontWeight: 700, fontFamily: 'var(--font-display)' }}>Novo grupo</h2>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn-fechar-modal" onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted, #6b7280)' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--muted, #6b7280)', display: 'block', marginBottom: 4 }}>Nome do grupo</label>
            <UpperInput value={nome} onChange={setNome} autoFocus placeholder="ex: FLEX, KIT DPS, PP"
              onKeyDown={(e) => { if (e.key === 'Enter') void criar(); }}
              style={{ width: '100%', padding: '8px 11px', borderRadius: 8 } as CSSProperties} />
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted, #6b7280)', margin: 0 }}>
            O grupo e um container que junta familias distintas (ex: DPS 32A, FLEX 2,5mm, Terminal). Depois de criado, use "Adicionar familia" no cabecalho do grupo.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn-secundario" onClick={onFechar} style={{ padding: '7px 14px' }}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={criar} disabled={salvando || !nome.trim()} style={{ padding: '7px 16px' }}>Criar grupo</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Larguras das colunas das tabelas dos blocos — FIXAS e iguais em todos, porque o cabecalho e'
// um so no topo da pagina (pedido dele, 07/10/2026) e so alinha se todo bloco usar a mesma grade.
// null = Descricao, a unica sem largura: absorve a sobra, e o texto dela ocupa a coluna inteira.
// Cada numero = o MAIOR entre o conteudo mais largo da coluna e o titulo dela, medido nos 38 blocos
// em prod (07/10): Custo 138 (selo MELHOR + numero). Codigo 102 (pedido dele, 28/10/2026; era 147
// pelo selo "cod. nao encontrado" numa linha so): o maior pedaco que nao quebra e o "encontrado" do
// selo (78px), mais 12+12 de padding; "TRAMONTINA GO" e o selo passam a ocupar duas linhas.
// Acoes 128 = cinco icones (o "mover para outra familia" entrou em 08/10).
// Pedidos dele de 28/10/2026 pra dar espaco a Descricao: UF de 124 pra 54 (empilha selo, "+DIFAL"
// e caixa da aliquota, centralizados, padding lateral curto); Pr. Forn. 114 -> 86 e Custo 139 -> 104
// (-25%). Nas duas o que nao cabe QUEBRA de linha em vez de invadir a vizinha: o selo MELHOR sobe
// acima do custo, o lapis acima do preco digitado, "estimado · site x N%" vira duas linhas.
// Soma das fixas 974 — numa janela de ~1500 sobra ~450 pra Descricao.
const LARGURAS_MONITOR: (number | null)[] = [42, 102, 55, null, 120, 76, 54, 86, 104, 114, 93, 128];

function ColunasMonitor() {
  return (
    <colgroup>
      {LARGURAS_MONITOR.map((w, i) => <col key={i} style={w == null ? undefined : { width: w }} />)}
    </colgroup>
  );
}

// Cabecalho UNICO das colunas, fixo no topo da area rolavel. top -20 = o padding do .app-content
// (medido: com 0 sobrava uma faixa de 20px por onde as linhas apareciam acima dele). A moldura de
// 1px e' a mesma do cartao do bloco: e' ela que deixa a tabela daqui com a largura exata das de la.
function CabecalhoColunasMonitor() {
  return (
    <div style={{ position: 'sticky', top: -20, zIndex: 5, marginBottom: 10, background: 'var(--panel-bg)', border: '1px solid var(--panel-border)', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <ColunasMonitor />
        <thead>
          <tr>
            {/* Letra PRETA no cabecalho fixo (pedido dele, 07/10/2026): o cinza do Th sumia no painel. */}
            <Th cor="#000"></Th>
            <Th cor="#000" align="center">Codigo</Th>
            <Th cor="#000"></Th>
            <Th cor="#000" align="left">Descricao (fornecedor)</Th>
            <Th cor="#000" align="left">Marca</Th>
            <Th cor="#000">Qtd emb.</Th>
            <Th cor="#000" align="center">UF</Th>
            <Th cor="#000">Pr. Forn.</Th>
            <Th cor="#000">Custo unit.<br />+DIFAL</Th>
            <Th cor="#000">Coletado</Th>
            <Th cor="#000">Sugestao<br />de Venda</Th>
            <Th cor="#000"></Th>
          </tr>
        </thead>
      </table>
    </div>
  );
}

function Th({ children, align = 'right', cor }: { children?: React.ReactNode; align?: 'left' | 'right' | 'center'; cor?: string }) {
  return (
    <th style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.3px', color: cor ?? 'var(--muted, #6b7280)', textAlign: align, padding: '8px 12px', fontWeight: 600, whiteSpace: 'nowrap', border: '1px solid #fff' }}>
      {children}
    </th>
  );
}
